import axios from "axios";
import { unauthorizedInterceptors } from "../../../core/utilities/functions";

export const accountingAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/accounting`,
  headers: {
    "Content-Type": "application/json",
  },
});
unauthorizedInterceptors(accountingAPI);

const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    accountingAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// --- Request Queue for Throttling Concurrent Requests ----------------------
// Browsers limit concurrent connections (~6 per domain). This queue ensures
// we don't overwhelm the connection limit, preventing stalled requests.
const MAX_CONCURRENT_REQUESTS = 5; // Increased from 3 - modern browsers handle 6+ well
let activeRequests = 0;
const highPriorityQueue = []; // For main data (invoices, transactions)
const lowPriorityQueue = []; // For secondary data (history, stats)

const processQueue = () => {
  while (activeRequests < MAX_CONCURRENT_REQUESTS) {
    // Process high priority first
    if (highPriorityQueue.length > 0) {
      const { resolve, requestFn } = highPriorityQueue.shift();
      activeRequests++;
      requestFn()
        .then((result) => {
          activeRequests--;
          resolve(result);
          processQueue();
        })
        .catch((err) => {
          activeRequests--;
          resolve(Promise.reject(err));
          processQueue();
        });
    } else if (lowPriorityQueue.length > 0) {
      const { resolve, requestFn } = lowPriorityQueue.shift();
      activeRequests++;
      requestFn()
        .then((result) => {
          activeRequests--;
          resolve(result);
          processQueue();
        })
        .catch((err) => {
          activeRequests--;
          resolve(Promise.reject(err));
          processQueue();
        });
    } else {
      break; // No more requests
    }
  }
};

// Wrap API calls to go through the queue
const queuedRequest = (requestFn, priority = "high") => {
  return new Promise((resolve) => {
    const queue = priority === "low" ? lowPriorityQueue : highPriorityQueue;
    queue.push({ resolve, requestFn });
    processQueue();
  });
};

// --- Quick First Page Functions for Hybrid Loading -------------------------
// These fetch ONLY the first page quickly for immediate display
// Use the regular "getAll*" functions for background loading of remaining pages

const FIRST_PAGE_SIZE = 500; // Reduced from 1000 for better mobile performance

// In-flight request tracking for first page functions to prevent duplicate concurrent requests
const firstPageInFlight = new Map();

const deduplicatedFirstPageRequest = async (key, requestFn) => {
  // Check if there's already an in-flight request for this key
  if (firstPageInFlight.has(key)) {
    return firstPageInFlight.get(key);
  }
  
  // Create and track the new request
  const requestPromise = requestFn().finally(() => {
    // Clean up after request completes (with a small delay to handle rapid re-requests)
    setTimeout(() => firstPageInFlight.delete(key), 100);
  });
  
  firstPageInFlight.set(key, requestPromise);
  return requestPromise;
};

export const getFirstPageBankStatements = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && key !== "page" && key !== "page_size") {
      params.append(key, value);
    }
  });
  params.set("page", "1");
  params.set("page_size", String(FIRST_PAGE_SIZE));

  const response = await accountingAPI.get(
    `transactions/?${params.toString()}`
  );
  const data = response?.data || response;
  
  // Debug: Check if overpayment data is coming through
  const resultsWithOverpay = (data?.results || []).filter(r => r.overpayment_id || r.active_overpayment_amount > 0);
  if (resultsWithOverpay.length > 0) {
    console.log('[DEBUG] Transactions with overpayment data:', resultsWithOverpay.map(r => ({
      id: r.id,
      overpayment_id: r.overpayment_id,
      overpayment_number: r.overpayment_number,
      active_overpayment_amount: r.active_overpayment_amount
    })));
  }
  
  return {
    results: data?.results || [],
    totalCount: data?.total || data?.count || 0,
    hasMore: (data?.results?.length || 0) >= FIRST_PAGE_SIZE,
  };
};

export const getFirstPageOverpayments = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && key !== "page" && key !== "page_size") {
      params.append(key, value);
    }
  });
  params.set("page", "1");
  params.set("page_size", String(FIRST_PAGE_SIZE));

  const url = `partner-overpayments/?${params.toString()}`;
  const response = await accountingAPI.get(url);
  const data = response?.data || response;
  
  // Handle different response structures:
  // 1. Paginated: { results: [...], count: X }
  // 2. Direct array: [...]
  let results = [];
  let totalCount = 0;
  
  if (Array.isArray(data)) {
    // Direct array response
    results = data;
    totalCount = data.length;
  } else if (data?.results) {
    // Paginated response with 'results'
    results = data.results;
    totalCount = data.total || data.count || data.results.length;
  } else if (data?.data) {
    // Response with 'data' wrapper
    results = data.data;
    totalCount = data.total || data.count || data.data.length;
  }
  
  return {
    results,
    totalCount,
    hasMore: results.length >= FIRST_PAGE_SIZE,
  };
};

export const getFirstPageCash = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && key !== "page" && key !== "page_size") {
      params.append(key, value);
    }
  });
  params.set("page", "1");
  params.set("page_size", String(FIRST_PAGE_SIZE));

  const response = await accountingAPI.get(`cash/?${params.toString()}`);
  const data = response?.data || response;
  return {
    results: data?.results || data?.data || [],
    totalCount: data?.total || data?.count || 0,
    hasMore:
      (data?.results?.length || data?.data?.length || 0) >= FIRST_PAGE_SIZE,
  };
};

export const getFirstPageCredits = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && key !== "page" && key !== "page_size") {
      params.append(key, value);
    }
  });
  params.set("page", "1");
  params.set("page_size", String(FIRST_PAGE_SIZE));

  const response = await accountingAPI.get(`credits/?${params.toString()}`);
  const data = response?.data || response;
  return {
    results: data?.results || data?.data || [],
    totalCount: data?.total || data?.count || 0,
    hasMore:
      (data?.results?.length || data?.data?.length || 0) >= FIRST_PAGE_SIZE,
  };
};

// First page for invoices (bills) - used for hybrid loading in Accounts Payable
export const getFirstPageInvoices = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && key !== "page" && key !== "page_size") {
      params.append(key, value);
    }
  });
  params.set("page", "1");
  params.set("page_size", String(FIRST_PAGE_SIZE));

  const response = await accountingAPI.get(`invoices/?${params.toString()}`);
  const data = response?.data || response;
  return {
    results: data?.results || data?.data || [],
    totalCount: data?.total || data?.count || 0,
    hasMore:
      (data?.results?.length || data?.data?.length || 0) >= FIRST_PAGE_SIZE,
  };
};

// First page for credits (for Accounts Payable) - used for hybrid loading
export const getFirstPageCreditsForBills = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && key !== "page" && key !== "page_size") {
      params.append(key, value);
    }
  });
  params.set("page", "1");
  params.set("page_size", String(FIRST_PAGE_SIZE));

  const response = await accountingAPI.get(`credits/?${params.toString()}`);
  const data = response?.data || response;
  return {
    results: data?.results || data?.data || [],
    totalCount: data?.total || data?.count || 0,
    hasMore:
      (data?.results?.length || data?.data?.length || 0) >= FIRST_PAGE_SIZE,
  };
};

// --- Fast First Page Functions for Allocation Modals -----------------------
// These fetch ONLY the first page of invoices for allocation modals
// allowing immediate display while remaining data loads in background

const ALLOCATION_FIRST_PAGE_SIZE = 100; // Smaller for faster initial display in modals

export const getFirstPagePartnerInvoicesForPayment = async (params = {}) => {
  setAuthHeader();
  
  // Create unique key for deduplication
  const dedupeKey = `payment-invoices-${JSON.stringify(params)}`;
  
  return deduplicatedFirstPageRequest(dedupeKey, async () => {
    const response = await accountingAPI.get(
      `payment-allocations/partner_invoices/`,
      { params: { ...params, page: 1, page_size: ALLOCATION_FIRST_PAGE_SIZE } }
    );
    const data = response?.data || {};
    const results = data?.results || data?.data || (Array.isArray(data) ? data : []);
    return {
      results,
      totalCount: data?.count || results.length,
      hasMore: results.length >= ALLOCATION_FIRST_PAGE_SIZE,
    };
  });
};

export const getFirstPagePartnerInvoicesForCredit = async (params = {}) => {
  setAuthHeader();
  
  // Create unique key for deduplication
  const dedupeKey = `credit-invoices-${JSON.stringify(params)}`;
  
  return deduplicatedFirstPageRequest(dedupeKey, async () => {
    const response = await accountingAPI.get(
      `credit-allocations/partner_invoices/`,
      { params: { ...params, page: 1, page_size: ALLOCATION_FIRST_PAGE_SIZE } }
    );
    const data = response?.data || {};
    const results = data?.results || data?.data || (Array.isArray(data) ? data : []);
    return {
      results,
      totalCount: data?.count || results.length,
      hasMore: results.length >= ALLOCATION_FIRST_PAGE_SIZE,
    };
  });
};

export const getFirstPagePartnerInvoicesForCash = async (params = {}) => {
  setAuthHeader();
  
  // Create unique key for deduplication
  const dedupeKey = `cash-invoices-${JSON.stringify(params)}`;
  
  return deduplicatedFirstPageRequest(dedupeKey, async () => {
    const response = await accountingAPI.get(
      `cash-allocations/partner_invoices/`,
      { params: { ...params, page: 1, page_size: ALLOCATION_FIRST_PAGE_SIZE } }
    );
    const data = response?.data || {};
    const results = data?.results || data?.data || (Array.isArray(data) ? data : []);
    return {
      results,
      totalCount: data?.count || results.length,
      hasMore: results.length >= ALLOCATION_FIRST_PAGE_SIZE,
    };
  });
};

export const getFirstPageUnpaidInvoicesForOverpayment = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (filters.brand) params.append("brand", filters.brand);
  if (filters.partner_id) params.append("partner_id", filters.partner_id);
  if (filters.overpayment_id) params.append("overpayment_id", filters.overpayment_id);
  if (filters.show_paid_invoices) params.append("show_paid_invoices", filters.show_paid_invoices);
  params.set("page", "1");
  params.set("page_size", String(ALLOCATION_FIRST_PAGE_SIZE));

  // Create unique key for deduplication
  const dedupeKey = `overpayment-invoices-${params.toString()}`;
  
  return deduplicatedFirstPageRequest(dedupeKey, async () => {
    const response = await accountingAPI.get(
      `overpayment-allocations/unpaid_invoices/?${params}`
    );
    const data = response?.data || {};
    const results = data?.results || data?.data || (Array.isArray(data) ? data : []);
    return {
      results,
      totalCount: data?.count || results.length,
      hasMore: results.length >= ALLOCATION_FIRST_PAGE_SIZE,
    };
  });
};

// --- Fast First Page for Journal Entries and General Ledger -----------------
const JOURNAL_FIRST_PAGE_SIZE = 1000; // Load first 1000 entries for quick display

export const getFirstPageJournalEntries = async (params = {}) => {
  setAuthHeader();
  const requestParams = new URLSearchParams();
  if (params.brandIds?.length) {
    params.brandIds.forEach((id) => requestParams.append("brand_ids[]", id));
  }
  if (params.start) requestParams.append("start", params.start);
  if (params.end) requestParams.append("end", params.end);
  if (params.type) requestParams.append("entry_type", params.type);
  if (params.search) requestParams.append("search", params.search);
  requestParams.set("page", "1");
  requestParams.set("page_size", String(JOURNAL_FIRST_PAGE_SIZE));

  const response = await accountingAPI.get(`/api/journal/?${requestParams.toString()}`);
  const data = response?.data || {};
  return {
    results: data?.results || data?.data || (Array.isArray(data) ? data : []),
    totalCount: data?.count || 0,
    hasMore: (data?.results?.length || data?.data?.length || 0) >= JOURNAL_FIRST_PAGE_SIZE,
  };
};

export const getFirstPageGeneralLedger = async (params = {}) => {
  setAuthHeader();
  const requestParams = new URLSearchParams();
  if (params.brandIds?.length) {
    params.brandIds.forEach((id) => requestParams.append("brand_ids[]", id));
  }
  if (params.start) requestParams.append("start", params.start);
  if (params.end) requestParams.append("end", params.end);
  if (params.account) requestParams.append("account", params.account);
  requestParams.set("page", "1");
  requestParams.set("page_size", String(JOURNAL_FIRST_PAGE_SIZE));

  // Use the same journal endpoint - GeneralLedger just filters/groups the data differently
  const response = await accountingAPI.get(`/api/journal/?${requestParams.toString()}`);
  const data = response?.data || {};
  return {
    results: data?.results || data?.data || (Array.isArray(data) ? data : []),
    totalCount: data?.count || 0,
    hasMore: (data?.results?.length || data?.data?.length || 0) >= JOURNAL_FIRST_PAGE_SIZE,
  };
};

// --- Lightweight GET cache & de‑duplication -------------------------------
// This prevents multiple identical GET requests (same URL + query string)
// from firing when several components mount at once with the same filters.
const getCache = new Map();
const inFlightGet = new Map();
const DEFAULT_GET_TTL_MS = 30000; // 30 seconds

const buildGetKey = (url) => `GET ${url}`;

export const clearAccountingCache = () => {
  getCache.clear();
};

// Clear specific cache entries by prefix (e.g., "partners/" to clear all partner cache)
export const clearCacheByPrefix = (prefix) => {
  for (const key of getCache.keys()) {
    if (key.includes(prefix)) {
      getCache.delete(key);
    }
  }
};

// Convenience functions to clear specific data caches
export const clearPartnersCache = () => clearCacheByPrefix("partners/");
export const clearCategoriesCache = () => clearCacheByPrefix("categories/");
export const clearAccountsCache = () => clearCacheByPrefix("accounts/");

const cachedGetData = async (
  relativeUrl,
  { ttlMs = DEFAULT_GET_TTL_MS, skipQueue = false } = {}
) => {
  setAuthHeader();

  const url = relativeUrl.startsWith("/") ? relativeUrl.slice(1) : relativeUrl;
  const key = buildGetKey(url);
  const now = Date.now();

  const cached = getCache.get(key);
  if (cached && now - cached.time < ttlMs) {
    return cached.data;
  }

  if (inFlightGet.has(key)) {
    return inFlightGet.get(key);
  }

  // Use queued request to prevent overwhelming browser connections
  const makeRequest = () =>
    accountingAPI
      .get(url)
      .then((response) => {
        getCache.set(key, { data: response.data, time: Date.now() });
        inFlightGet.delete(key);
        return response.data;
      })
      .catch((err) => {
        inFlightGet.delete(key);
        throw err;
      });

  // Skip queue for cached/deduped requests, use queue for fresh requests
  const requestPromise = skipQueue ? makeRequest() : queuedRequest(makeRequest);

  inFlightGet.set(key, requestPromise);
  return requestPromise;
};

// Product API Functions - CACHED (2 min TTL)
export const getAllProducts = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.name) params.append("name", filters.name);
  if (filters.brand) params.append("brand", filters.brand);
  if (filters.vendor) params.append("vendor", filters.vendor);

  const queryString = params.toString();
  const url = `products/${queryString ? "?" + queryString : ""}`;

  const data = await cachedGetData(url, { ttlMs: 2 * 60 * 1000 });
  return { data };
};

export const getProductById = async (productId) => {
  setAuthHeader();
  return await accountingAPI.get(`products/${productId}/`);
};

export const createProduct = async (productData) => {
  setAuthHeader();
  return await accountingAPI.post("products/", productData);
};

export const updateProduct = async (productId, productData) => {
  setAuthHeader();
  return await accountingAPI.put(`products/${productId}/`, productData);
};

export const deleteProduct = async (productId) => {
  setAuthHeader();
  return await accountingAPI.delete(`products/${productId}/`);
};

// Category API Functions - CACHED (5 min TTL)
export const getAllCategories = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.brand) params.append("brand", filters.brand);
  if (filters.vendor) params.append("vendor", filters.vendor);

  const queryString = params.toString();
  const url = `categories/${queryString ? "?" + queryString : ""}`;

  // Use 5 minute cache for categories (rarely change)
  const data = await cachedGetData(url, { ttlMs: 5 * 60 * 1000 });
  return { data }; // Wrap in {data} to match expected response format
};

export const getTopLevelCategories = async () => {
  setAuthHeader();
  return await accountingAPI.get("categories/?parent=null");
};

export const getCategoryTree = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (filters.brand) params.append("brand", filters.brand);

  const queryString = params.toString();
  return await accountingAPI.get(
    `categories/tree/${queryString ? "?" + queryString : ""}`
  );
};

export const getCategoryById = async (categoryId) => {
  setAuthHeader();
  return await accountingAPI.get(`categories/${categoryId}/`);
};

export const createCategory = async (categoryData) => {
  setAuthHeader();
  return await accountingAPI.post("categories/", categoryData);
};

export const updateCategory = async (categoryId, categoryData) => {
  setAuthHeader();
  return await accountingAPI.put(`categories/${categoryId}/`, categoryData);
};

export const deleteCategory = async (categoryId) => {
  setAuthHeader();
  return await accountingAPI.delete(`categories/${categoryId}/`);
};

// Partner (Vendor) API Functions - CACHED (2 min TTL)
export const getAllPartners = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.name) params.append("name", filters.name);
  if (filters.type) params.append("type", filters.type);
  if (filters.active !== undefined) params.append("active", filters.active);
  if (filters.brand) params.append("brand", filters.brand);

  const queryString = params.toString();
  const url = `partners/${queryString ? "?" + queryString : ""}`;

  // Use 2 minute cache for partners - fetches all at once, pagination is client-side
  const data = await cachedGetData(url, { ttlMs: 2 * 60 * 1000 });
  return { data }; // Wrap in {data} to match expected response format
};

// CACHED (2 min TTL)
export const getVendors = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.name) params.append("name", filters.name);
  if (filters.active !== undefined) params.append("active", filters.active);
  if (filters.brand) params.append("brand", filters.brand);

  const queryString = params.toString();
  const url = `partners/vendors/${queryString ? "?" + queryString : ""}`;

  const data = await cachedGetData(url, { ttlMs: 2 * 60 * 1000 });
  return { data };
};

export const getPartnerById = async (partnerId) => {
  setAuthHeader();
  return await accountingAPI.get(`partners/${partnerId}/`);
};

export const createPartner = async (partnerData) => {
  setAuthHeader();
  return await accountingAPI.post("partners/", partnerData);
};

export const updatePartner = async (partnerId, partnerData) => {
  setAuthHeader();
  return await accountingAPI.put(`partners/${partnerId}/`, partnerData);
};

export const deletePartner = async (partnerId) => {
  setAuthHeader();
  return await accountingAPI.delete(`partners/${partnerId}/`);
};

// Payment Terms API Functions
export const getAllPaymentTerms = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (filters.name) params.append("name", filters.name);
  if (filters.active !== undefined) params.append("active", filters.active);
  if (filters.brand) params.append("brand", filters.brand);

  const queryString = params.toString();
  return await accountingAPI.get(
    `payment-terms/${queryString ? "?" + queryString : ""}`
  );
};

export const getPaymentTermById = async (termId) => {
  setAuthHeader();
  return await accountingAPI.get(`payment-terms/${termId}/`);
};

export const createPaymentTerm = async (termData) => {
  setAuthHeader();
  return await accountingAPI.post("payment-terms/", termData);
};

export const updatePaymentTerm = async (termId, termData) => {
  setAuthHeader();
  return await accountingAPI.put(`payment-terms/${termId}/`, termData);
};

export const deletePaymentTerm = async (termId) => {
  setAuthHeader();
  return await accountingAPI.delete(`payment-terms/${termId}/`);
};

// Bank API Functions
export const getAllBanks = async (filters = {}) => {
  setAuthHeader();
  try {
    const params = new URLSearchParams();
    if (filters.name) params.append("name", filters.name);
    if (filters.active !== undefined) params.append("active", filters.active);
    if (filters.brand) params.append("brand", filters.brand);

    const queryString = params.toString();
    const response = await accountingAPI.get(
      `banks/${queryString ? "?" + queryString : ""}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching banks:", error);
    throw error;
  }
};

export const getBankById = async (bankId) => {
  setAuthHeader();
  return await accountingAPI.get(`banks/${bankId}/`);
};

export const createBank = async (bankData) => {
  setAuthHeader();
  return await accountingAPI.post("banks/", bankData);
};

export const updateBank = async (bankId, bankData) => {
  setAuthHeader();
  return await accountingAPI.put(`banks/${bankId}/`, bankData);
};

export const deleteBank = async (bankId) => {
  setAuthHeader();
  return await accountingAPI.delete(`banks/${bankId}/`);
};

// Invoice API Functions
// Invoice data cached for 15 seconds (changes frequently)
export const getAllInvoices = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    // Handle date range filters specifically
    if (filters.dateRange && Array.isArray(filters.dateRange)) {
      if (filters.dateRange[0])
        params.append("start_date", filters.dateRange[0]);
      if (filters.dateRange[1]) params.append("end_date", filters.dateRange[1]);
    }

    // Handle other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== "dateRange") params.append(key, value);
    });

    const query = params.toString();
    const path = `invoices/${query ? "?" + query : ""}`;
    // Use shorter 15s cache for invoices since they change frequently
    const data = await cachedGetData(path, { ttlMs: 15000 });
    return data;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

/**
 * Fetch ALL invoices in a single request (optimized for large datasets 6000+ records)
 * Uses a large page_size to minimize HTTP requests and overhead
 * @param {Object} filters - Filter parameters
 * @param {number} maxRecords - Maximum records to fetch (default 50000)
 * @returns {Promise<Object>} - Results with all invoices
 */
// Progressive loading: fetch 1000 at a time, call callback with each batch
export const getAllInvoicesProgressive = async (
  filters = {},
  onBatch = null
) => {
  try {
    const batchSize = 1000; // Fetch 1000 records per batch
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;
    let firstBatchDelivered = false;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();

    // Handle date range filters specifically
    if (filters.dateRange && Array.isArray(filters.dateRange)) {
      if (filters.dateRange[0])
        baseParams.append("start_date", filters.dateRange[0]);
      if (filters.dateRange[1])
        baseParams.append("end_date", filters.dateRange[1]);
    }

    // Handle other filters (exclude pagination params)
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value &&
        key !== "dateRange" &&
        key !== "page" &&
        key !== "page_size"
      ) {
        baseParams.append(key, value);
      }
    });

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(batchSize));

      const path = `invoices/?${params.toString()}`;

      const data = await cachedGetData(path, { ttlMs: 30000 });

      const pageResults = data?.results || [];
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Call callback with batch if provided - use setTimeout to allow UI to update
      if (onBatch && typeof onBatch === "function") {
        const isFirst = !firstBatchDelivered;
        if (isFirst) {
          // First batch: call immediately
          onBatch(pageResults, {
            currentPage,
            totalFetched: allResults.length,
            totalCount: totalCount || allResults.length,
            isFirstBatch: true,
          });
          firstBatchDelivered = true;
        } else {
          // Subsequent batches: allow UI to update between batches
          await new Promise((resolve) => setTimeout(resolve, 0));
          onBatch(pageResults, {
            currentPage,
            totalFetched: allResults.length,
            totalCount: totalCount || allResults.length,
            isFirstBatch: false,
          });
        }
      }

      // Check if we have more pages to fetch
      if (pageResults.length === 0) {
        hasMore = false;
      } else if (pageResults.length === batchSize) {
        currentPage++;
        if (currentPage > 1000) {
          hasMore = false;
        }
      } else if (pageResults.length < batchSize) {
        if (allResults.length >= totalCount && totalCount > 0) {
          hasMore = false;
        } else {
          currentPage++;
          if (currentPage > 1000) {
            hasMore = false;
          }
        }
      } else {
        currentPage++;
      }
    }

    return { results: allResults, count: totalCount || allResults.length };
  } catch (error) {
    console.error("Error fetching all invoices:", error);
    throw error;
  }
};

export const getAllInvoicesParallel = async (filters = {}) => {
  try {
    const pageSize = 1000; // Page size for fetching
    setAuthHeader();

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();

    // Handle date range filters specifically
    if (filters.dateRange && Array.isArray(filters.dateRange)) {
      if (filters.dateRange[0])
        baseParams.append("start_date", filters.dateRange[0]);
      if (filters.dateRange[1])
        baseParams.append("end_date", filters.dateRange[1]);
    }

    // Handle other filters (exclude pagination params)
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value &&
        key !== "dateRange" &&
        key !== "page" &&
        key !== "page_size"
      ) {
        baseParams.append(key, value);
      }
    });

    // Step 1: Fetch first page to get total count
    const firstPageParams = new URLSearchParams(baseParams);
    firstPageParams.set("page", "1");
    firstPageParams.set("page_size", String(pageSize));
    const firstPagePath = `invoices/?${firstPageParams.toString()}`;

    // Use queuedRequest to prevent overwhelming browser connections
    const firstPageData = await queuedRequest(() =>
      accountingAPI.get(firstPagePath)
    );
    const firstPageResults = firstPageData?.data?.results || [];
    const totalCount =
      firstPageData?.data?.count || firstPageData?.data?.total || 0;

    // If we got less than a full page, we have all results
    if (firstPageResults.length < pageSize) {
      return {
        results: firstPageResults,
        count: totalCount || firstPageResults.length,
      };
    }

    // Step 2: Calculate total pages needed based on count
    // Use totalCount if available, otherwise estimate conservatively
    const estimatedTotal =
      totalCount > 0 ? totalCount : firstPageResults.length * 10;
    const totalPages = Math.min(Math.ceil(estimatedTotal / pageSize), 50); // Max 50 pages = 50k records

    if (totalPages <= 1) {
      return {
        results: firstPageResults,
        count: totalCount || firstPageResults.length,
      };
    }

    // Step 3: Fetch ALL remaining pages in parallel
    const remainingPages = [];
    for (let page = 2; page <= totalPages; page++) {
      remainingPages.push(page);
    }

    const pagePromises = remainingPages.map(async (pageNum) => {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(pageNum));
      params.set("page_size", String(pageSize));
      const path = `invoices/?${params.toString()}`;

      try {
        // Use queuedRequest to prevent overwhelming browser connections
        const response = await queuedRequest(() => accountingAPI.get(path));
        return response?.data?.results || [];
      } catch (err) {
        console.error(`Error fetching invoices page ${pageNum}:`, err);
        return [];
      }
    });

    const pageResults = await Promise.all(pagePromises);

    // Combine all results
    let allResults = [...firstPageResults];
    for (const results of pageResults) {
      if (results.length > 0) {
        allResults = allResults.concat(results);
      }
    }

    return { results: allResults, count: totalCount || allResults.length };
  } catch (error) {
    console.error("Error fetching all invoices:", error);
    throw error;
  }
};

// Fetch unique filter values for invoice filters
export const getInvoiceFilterValues = async (
  filterType = "all",
  brandId = null
) => {
  try {
    setAuthHeader();
    const params = new URLSearchParams();

    if (filterType && filterType !== "all") {
      params.append("filter_type", filterType);
    }

    if (brandId) {
      params.append("brand", brandId);
    }

    const response = await accountingAPI.get(
      `invoices/filter-values/${
        params.toString() ? "?" + params.toString() : ""
      }`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching filter values:", error);
    throw error;
  }
};

// Fetch unique filter values for bank statement filters
export const getBankStatementFilterValues = async (brandId = null) => {
  try {
    setAuthHeader();
    const params = new URLSearchParams();
    if (brandId) {
      params.append("brand", brandId);
    }
    const response = await accountingAPI.get(
      `bank-statements/filter-values/${
        params.toString() ? "?" + params.toString() : ""
      }`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching bank statement filter values:", error);
    throw error;
  }
};

export const getInvoiceById = async (id) => {
  try {
    const response = await accountingAPI.get(`invoices/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching invoice ${id}:`, error);
    throw error;
  }
};

export const createInvoice = async (invoiceData) => {
  try {
    const response = await accountingAPI.post("invoices/", invoiceData);
    return response.data;
  } catch (error) {
    console.error("Error creating invoice:", error);
    throw error;
  }
};

export const updateInvoice = async (id, invoiceData) => {
  try {
    const response = await accountingAPI.put(`invoices/${id}/`, invoiceData);
    return response.data;
  } catch (error) {
    console.error(`Error updating invoice ${id}:`, error);
    throw error;
  }
};

export const createInvoiceWithFile = async (formData) => {
  try {
    // Send the request as multipart/form-data
    const token = localStorage.getItem("accessToken");
    // Use v2 endpoint if specified

    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/accounting/invoices/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating invoice with file:", error);
    throw error;
  }
};
export const exportCredits = async (filters) => {
  try {
    setAuthHeader();

    const response = await accountingAPI.post("credits/export/", filters, {
      responseType: "blob",
      timeout: 60000,
    });

    return response;
  } catch (error) {
    console.error("Error exporting credits:", error);
    throw error;
  }
};

export const exportVendorInvoiceStatistics = async (brands, filters = {}) => {
  try {
    setAuthHeader();

    const response = await accountingAPI.post(
      "invoices/vendor-statistics/export/",
      {
        brands,
        filters,
      },
      {
        responseType: "blob",
        timeout: 300000,
      }
    );

    return response;
  } catch (error) {
    console.error("Error exporting vendor statistics:", error);

    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      throw new Error(
        "Export timed out due to large dataset. Please try reducing the date range or applying more filters to limit the data size."
      );
    } else if (error.response?.status === 500) {
      throw new Error(
        "Server error occurred during export. Please try again or contact support if the issue persists."
      );
    } else if (error.request && !error.response) {
      throw new Error(
        "No response received from server. Please check your internet connection and try again."
      );
    }

    throw error;
  }
};

export const exportStatementHistory = async (filters) => {
  try {
    setAuthHeader();

    const response = await accountingAPI.post(
      "statements/history/export/",
      filters,
      {
        responseType: "blob",
        timeout: 60000,
      }
    );

    return response;
  } catch (error) {
    console.error("Error exporting statement history:", error);
    throw error;
  }
};
export const exportVendorAgingReport = async (brands, filters = {}) => {
  try {
    setAuthHeader();

    const response = await accountingAPI.post(
      "invoices/vendor-aging-report/export/",
      {
        brands,
        filters,
      },
      {
        responseType: "blob",
        timeout: 60000,
      }
    );

    return response;
  } catch (error) {
    console.error("Error exporting vendor aging report:", error);
    throw error;
  }
};

export const exportCash = async (filters) => {
  try {
    setAuthHeader();

    // Build query parameters from filters
    const params = new URLSearchParams();
    if (filters.brand) params.append("brand", filters.brand);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);
    if (filters.include_allocations)
      params.append("include_allocations", filters.include_allocations);

    const queryString = params.toString();
    const url = `cash/export/${queryString ? `?${queryString}` : ""}`;

    const response = await accountingAPI.get(url, {
      responseType: "blob",
      timeout: 60000,
    });

    return response;
  } catch (error) {
    console.error("Error exporting cash:", error);
    throw error;
  }
};

export const exportInvoices = async (filters) => {
  try {
    setAuthHeader();
    const response = await accountingAPI.post("invoices/export/", filters, {
      responseType: "blob", // Important for file downloads
    });
    return response;
  } catch (error) {
    console.error("Error exporting invoices:", error);
    throw error;
  }
};

export const exportBankStatements = async (filters) => {
  try {
    setAuthHeader();

    const response = await accountingAPI.post("statements/export/", filters, {
      responseType: "blob",
      timeout: 60000,
    });

    return response;
  } catch (error) {
    console.error("Error exporting bank statements:", error);
    throw error;
  }
};
export const updateInvoiceWithFile = async (id, formData) => {
  try {
    // Send the request as multipart/form-data
    const token = localStorage.getItem("accessToken");
    const response = await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/accounting/invoices/${id}/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating invoice ${id} with file:`, error);
    throw error;
  }
};

export const batchUpdateInvoices = async (invoiceIds, updates) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post("invoices/batch-update/", {
      invoice_ids: invoiceIds,
      ...updates,
    });
    return response.data;
  } catch (error) {
    console.error("Error batch updating invoices:", error);
    throw error;
  }
};

export const deleteInvoice = async (id) => {
  try {
    await accountingAPI.delete(`invoices/${id}/`);
    return true;
  } catch (error) {
    console.error(`Error deleting invoice ${id}:`, error);
    throw error;
  }
};

export const approveInvoice = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(`invoices/${id}/approve/`);
    return response.data;
  } catch (error) {
    console.error(`Error approving invoice ${id}:`, error);
    throw error;
  }
};

export const rejectInvoice = async (id, rejectionReason = "") => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(`invoices/${id}/reject/`, {
      rejection_reason: rejectionReason,
    });
    return response.data;
  } catch (error) {
    console.error(`Error rejecting invoice ${id}:`, error);
    throw error;
  }
};

export const splitInvoice = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(`invoices/${id}/split/`);
    return response.data;
  } catch (error) {
    console.error(`Error splitting invoice ${id}:`, error);
    throw error;
  }
};

export const exportInvoicePDF = async (id) => {
  setAuthHeader();

  // Get custom rates from localStorage
  let customRates = {};
  try {
    const savedRates = localStorage.getItem("custom_currency_rates");
    if (savedRates) {
      customRates = JSON.parse(savedRates);
    }
  } catch (e) {
    console.warn("Could not load custom currency rates:", e);
  }

  const response = await accountingAPI.get(`invoices/${id}/export-pdf/`, {
    responseType: "blob",
    params: {
      custom_rates: JSON.stringify(customRates),
    },
  });
  return response;
};

export const exportCreditPDF = async (id) => {
  setAuthHeader();

  // Get custom rates from localStorage
  let customRates = {};
  try {
    const savedRates = localStorage.getItem("custom_currency_rates");
    if (savedRates) {
      customRates = JSON.parse(savedRates);
    }
  } catch (e) {
    console.warn("Could not load custom currency rates:", e);
  }

  const response = await accountingAPI.get(`credits/${id}/export-pdf/`, {
    responseType: "blob",
    params: {
      custom_rates: JSON.stringify(customRates),
    },
  });
  return response;
};

export const updateInvoiceStatus = async (id, status) => {
  try {
    const response = await accountingAPI.patch(`invoices/${id}/status/`, {
      status,
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating invoice ${id} status:`, error);
    throw error;
  }
};

// Statement API functions
export const getAllStatements = async (filters = {}) => {
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== "page" && key !== "page_size") {
        baseParams.append(key, value);
      }
    });

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const response = await accountingAPI.get(`statements/?${params}`);
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching statements:", error);
    throw error;
  }
};

export const getStatementById = async (id) => {
  try {
    const response = await accountingAPI.get(`statements/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching statement ${id}:`, error);
    throw error;
  }
};

export const processStatementFile = async (fileObj, statementData) => {
  try {
    const token = localStorage.getItem("accessToken");

    // Create FormData with file and statement basic info
    const formData = new FormData();
    formData.append("file", fileObj);

    // Append statement data
    Object.keys(statementData).forEach((key) => {
      if (statementData[key] !== undefined && statementData[key] !== null) {
        formData.append(key, statementData[key]);
      }
    });

    // Send to backend for processing
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/accounting/statements/process_file/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error processing statement file:", error);
    if (error.response && error.response.data) {
      console.error("Error response data:", error.response.data);
    }
    throw error;
  }
};

export const createStatement = async (statementData) => {
  try {
    // First create the statement with transactions
    const token = localStorage.getItem("accessToken");

    // Create a copy of the data without the file
    const statementDataCopy = { ...statementData };

    // Create the statement with transactions (file is optional and handled in backend)
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/accounting/statements/`,
      statementDataCopy,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error creating statement:", error);
    if (error.response && error.response.data) {
      console.error("Error response data:", error.response.data);
    }
    throw error;
  }
};

export const updateStatement = async (id, statementData) => {
  try {
    const token = localStorage.getItem("accessToken");

    // Update the statement data with transactions (file is optional and handled in backend)
    const response = await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/accounting/statements/${id}/`,
      statementData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(`Error updating statement ${id}:`, error);
    if (error.response && error.response.data) {
      console.error("Error response data:", error.response.data);
    }
    throw error;
  }
};

export const deleteStatement = async (id) => {
  try {
    await accountingAPI.delete(`statements/${id}/`);
    return true;
  } catch (error) {
    console.error(`Error deleting statement ${id}:`, error);
    throw error;
  }
};

// Transaction API functions
export const getAllTransactions = async (filters = {}) => {
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== "page" && key !== "page_size") {
        baseParams.append(key, value);
      }
    });

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const response = await accountingAPI.get(`transactions/?${params}`);
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching transactions:", error);
    throw error;
  }
};

// Delete a single bank transaction by ID
// Backend checks for allocations and will reject if there are any
export const deleteTransaction = async (id) => {
  setAuthHeader();

  // Validate ID before making request
  if (!id || id === "undefined" || id === "null") {
    throw new Error(`Invalid transaction ID: ${id}`);
  }

  // Ensure ID is a number
  const numericId = typeof id === "string" ? parseInt(id, 10) : id;
  if (isNaN(numericId)) {
    throw new Error(`Transaction ID must be numeric, got: ${id}`);
  }

  try {
    const response = await accountingAPI.delete(`transactions/${numericId}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting transaction ${numericId}:`, error);
    throw error;
  }
};

// Cache for partner invoices - separate from general cache for fine-grained control
const partnerInvoicesCache = new Map();
const PARTNER_INVOICES_TTL = 20 * 1000; // 20 seconds - short TTL since invoices change on allocation

export const getPartnerInvoices = async (params = {}) => {
  try {
    setAuthHeader();

    // Build cache key from relevant params
    const cacheKey = `invoices-${params.partner}-${params.brand}-${
      params.transaction_id
    }-${params.show_paid_invoices || false}`;

    // Check cache first
    const cached = partnerInvoicesCache.get(cacheKey);
    if (cached && Date.now() - cached.time < PARTNER_INVOICES_TTL) {
      return cached.data;
    }

    // Use smaller page size for faster individual requests
    const pageSize = 500;

    // Build base params (without pagination)
    const baseParams = { ...params };
    delete baseParams.page;
    delete baseParams.page_size;

    // First request to get total count
    const firstResponse = await accountingAPI.get(
      `payment-allocations/partner_invoices/`,
      {
        params: { ...baseParams, page: 1, page_size: pageSize },
      }
    );
    const firstData = firstResponse?.data || {};
    const firstPageResults =
      firstData?.results || firstData?.data || (Array.isArray(firstData) ? firstData : []);
    const totalCount = firstData?.count || firstPageResults.length;

    // If all results fit in first page, return immediately
    if (firstPageResults.length >= totalCount || totalCount <= pageSize) {
      const result = {
        results: firstPageResults,
        count: totalCount,
        data: firstPageResults,
      };
      partnerInvoicesCache.set(cacheKey, { data: result, time: Date.now() });
      return result;
    }

    // Calculate remaining pages and fetch them in PARALLEL
    const totalPages = Math.ceil(totalCount / pageSize);
    const remainingPages = Array.from(
      { length: totalPages - 1 },
      (_, i) => i + 2
    );

    // Fetch all remaining pages in parallel (max 5 concurrent)
    const chunkSize = 5;
    let allResults = [...firstPageResults];

    for (let i = 0; i < remainingPages.length; i += chunkSize) {
      const pageChunk = remainingPages.slice(i, i + chunkSize);
      const pagePromises = pageChunk.map((page) =>
        accountingAPI.get(`payment-allocations/partner_invoices/`, {
          params: { ...baseParams, page, page_size: pageSize },
        })
      );

      const responses = await Promise.all(pagePromises);
      responses.forEach((response) => {
        const data = response?.data || {};
        const pageResults =
          data?.results || data?.data || (Array.isArray(data) ? data : []);
        allResults = [...allResults, ...pageResults];
      });
    }

    const result = {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };

    // Cache the results
    partnerInvoicesCache.set(cacheKey, { data: result, time: Date.now() });

    return result;
  } catch (error) {
    console.error("Error fetching partner invoices:", error);
    throw error;
  }
};

// Clear partner invoices cache (call after allocation/unallocation)
export const clearPartnerInvoicesCache = () => {
  partnerInvoicesCache.clear();
};

export const getCreditPartnerInvoices = async (params = {}) => {
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = { ...params };
    delete baseParams.page;
    delete baseParams.page_size;

    // Fetch all pages until we have all records
    while (hasMore) {
      const requestParams = {
        ...baseParams,
        page: currentPage,
        page_size: pageSize,
      };

      const response = await accountingAPI.get(
        `credit-allocations/partner_invoices/`,
        {
          params: requestParams,
        }
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching credit partner invoices:", error);
    throw error;
  }
};

export const getBankStatements = async (params) => {
  try {
    // Build query string from params
    const queryParams = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    const url = `transactions/${queryString ? "?" + queryString : ""}`;

    // Use 30s cache for bank statements (they change on allocation)
    const data = await cachedGetData(url, { ttlMs: 30 * 1000 });
    return data;
  } catch (error) {
    console.error("Error fetching bank statements:", error);
    throw error;
  }
};

/**
 * OPTIMIZED: Get partner invoices with server-side pagination
 * Returns only the requested page, with caching for fast subsequent requests
 *
 * @param {Object} params - Query parameters
 * @param {number} params.partner - Partner ID
 * @param {number} params.transaction_id - Transaction ID (for allocation context)
 * @param {number} params.brand - Brand ID
 * @param {number} params.page - Page number (default 1)
 * @param {number} params.page_size - Items per page (default 50)
 * @param {boolean} params.show_paid_invoices - Include paid invoices (default false)
 */
export const getPartnerInvoicesPaginated = async (params = {}) => {
  try {
    setAuthHeader();

    const requestParams = {
      ...params,
      page: params.page || 1,
      page_size: params.page_size || 50,
    };

    // Build cache key
    const cacheKey = `partner-invoices-${params.partner}-${params.brand}-${
      params.transaction_id
    }-${requestParams.page}-${requestParams.page_size}-${
      params.show_paid_invoices || false
    }`;

    // Check cache first (15 second TTL for invoices since they change on allocation)
    const cachedData = getCache.get(`GET ${cacheKey}`);
    if (cachedData && Date.now() - cachedData.time < 15 * 1000) {
      return cachedData.data;
    }

    const response = await accountingAPI.get(
      `payment-allocations/partner_invoices/`,
      { params: requestParams }
    );

    const data = response?.data || {};
    const results =
      data?.results || data?.data || (Array.isArray(data) ? data : []);
    const count = data?.count || results.length;

    const result = {
      results,
      count,
      data: results,
      page: requestParams.page,
      page_size: requestParams.page_size,
      total_pages: Math.ceil(count / requestParams.page_size),
    };

    // Cache the result
    getCache.set(`GET ${cacheKey}`, { data: result, time: Date.now() });

    return result;
  } catch (error) {
    console.error("Error fetching partner invoices:", error);
    throw error;
  }
};

// Fetch ALL bank statements without pagination limits (fetch all pages)
export const getAllBankStatementsParallel = async (filters = {}) => {
  try {
    // Remove limit from filters if present (we fetch all)
    delete filters.limit;

    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();

    // Handle other filters (exclude pagination params)
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== "page" && key !== "page_size") {
        baseParams.append(key, value);
      }
    });

    // Fetch first page immediately to get total count and show initial data
    setAuthHeader();
    const firstPageParams = new URLSearchParams(baseParams);
    firstPageParams.set("page", "1");
    firstPageParams.set("page_size", String(pageSize));
    const firstPageUrl = `transactions/?${firstPageParams.toString()}`;
    const cleanFirstUrl = firstPageUrl.startsWith("/")
      ? firstPageUrl.slice(1)
      : firstPageUrl;

    try {
      // Use queuedRequest to prevent overwhelming browser connections
      const firstPageResponse = await queuedRequest(() =>
        accountingAPI.get(cleanFirstUrl)
      );
      const firstPageData = firstPageResponse?.data || firstPageResponse;
      const firstPageResults = firstPageData?.results || [];
      // Backend returns 'total' not 'count'
      totalCount = firstPageData?.total || firstPageData?.count || 0;

      allResults = [...allResults, ...firstPageResults];

      // Always try to fetch more pages if first page has results
      // Continue until we get 2 consecutive empty batches
      if (firstPageResults.length > 0) {
        // Fetch pages in parallel batches for faster loading
        const parallelBatchSize = 3; // Fetch 3 pages at a time
        let nextPage = 2;
        const maxPages = 100; // Reduced safety limit - most datasets won't have 100+ pages of 1000
        let consecutiveEmptyBatches = 0; // Track consecutive empty batches

        while (nextPage <= maxPages) {
          const pageBatch = [];
          for (
            let i = 0;
            i < parallelBatchSize && nextPage + i <= maxPages;
            i++
          ) {
            const pageNum = nextPage + i;
            const params = new URLSearchParams(baseParams);
            params.set("page", String(pageNum));
            params.set("page_size", String(pageSize));
            const pageUrl = `transactions/?${params.toString()}`;
            const cleanUrl = pageUrl.startsWith("/")
              ? pageUrl.slice(1)
              : pageUrl;

            // Use queuedRequest to prevent overwhelming browser connections
            pageBatch.push(
              queuedRequest(() =>
                accountingAPI
                  .get(cleanUrl)
                  .then((response) => {
                    const responseData = response?.data || response;
                    const pageResults = responseData?.results || [];
                    return {
                      page: pageNum,
                      data: responseData,
                      results: pageResults,
                    };
                  })
                  .catch((err) => {
                    console.error(
                      `Error fetching transactions page ${pageNum}:`,
                      err?.message || err
                    );
                    return { page: pageNum, data: null, results: [] };
                  })
              )
            );
          }

          // Wait for batch to complete
          const batchResults = await Promise.all(pageBatch);

          // Process results
          let batchHasResults = false;
          for (const batchResult of batchResults) {
            if (batchResult.results && batchResult.results.length > 0) {
              allResults = [...allResults, ...batchResult.results];
              batchHasResults = true;
            }
          }

          // If batch had no results, increment counter
          if (!batchHasResults) {
            consecutiveEmptyBatches++;
            // Stop only after 2 consecutive empty batches to be sure
            if (consecutiveEmptyBatches >= 2) {
              break;
            }
          } else {
            // Reset counter if we got results
            consecutiveEmptyBatches = 0;
          }

          // Move to next batch
          nextPage += parallelBatchSize;
        }
      }
    } catch (error) {
      console.error("Error fetching bank statements:", error?.message || error);
      // Return what we have so far (might be empty)
    }

    // Return the actual fetched count, but also include the backend's reported total for reference
    return {
      results: allResults,
      count: allResults.length, // Use actual fetched count
      total: totalCount, // Include backend's reported total for reference
      hasMore: false, // We fetched everything
    };
  } catch (error) {
    console.error("Error fetching bank statements:", error);
    throw error;
  }
};

/**
 * OPTIMIZED: Fetch unified transactions using server-side pagination
 * This uses the UnifiedTransactionsView backend endpoint which:
 * - Combines bank statements, overpayments, cash, and credits in one query
 * - Handles pagination server-side for much faster loading
 * - Returns pre-formatted data ready for display
 *
 * @param {Object} params - Query parameters
 * @param {string} params.brand_ids - Comma-separated brand IDs (required)
 * @param {number} params.page - Page number (default 1)
 * @param {number} params.page_size - Items per page (default 50)
 * @param {boolean} params.include_bank - Include bank statements (default true)
 * @param {boolean} params.include_overpayment - Include overpayments (default true)
 * @param {boolean} params.include_cash - Include cash payments (default true)
 * @param {boolean} params.include_credit - Include credits (default true)
 * @param {string} params.start_date - Start date filter (YYYY-MM-DD)
 * @param {string} params.end_date - End date filter (YYYY-MM-DD)
 */
export const getUnifiedTransactions = async (params = {}) => {
  try {
    setAuthHeader();

    const queryParams = new URLSearchParams();

    // Required: brand_ids
    if (params.brand_ids) {
      queryParams.append("brand_ids", params.brand_ids);
    }

    // Pagination
    queryParams.append("page", String(params.page || 1));
    queryParams.append("page_size", String(params.page_size || 100));

    // Type filters
    if (params.include_bank !== undefined) {
      queryParams.append("include_bank", String(params.include_bank));
    }
    if (params.include_overpayment !== undefined) {
      queryParams.append(
        "include_overpayment",
        String(params.include_overpayment)
      );
    }
    if (params.include_cash !== undefined) {
      queryParams.append("include_cash", String(params.include_cash));
    }
    if (params.include_credit !== undefined) {
      queryParams.append("include_credit", String(params.include_credit));
    }

    // Date filters
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }

    const url = `api/unified-transactions/?${queryParams.toString()}`;

    // Use cache for repeated requests (30 second TTL)
    const data = await cachedGetData(url, {
      ttlMs: 30 * 1000,
      skipQueue: false,
    });

    return data;
  } catch (error) {
    console.error("Error fetching unified transactions:", error);
    throw error;
  }
};

/**
 * OPTIMIZED: Fetch all unified transactions for client-side pagination
 * Fetches data in batches for faster initial load, then continues in background
 *
 * @param {Object} params - Same as getUnifiedTransactions
 * @param {Function} onProgress - Callback for progress updates (receives partial data)
 */
export const getUnifiedTransactionsAll = async (
  params = {},
  onProgress = null
) => {
  try {
    setAuthHeader();

    const pageSize = 500; // Fetch in larger batches for efficiency
    let allResults = [];
    let page = 1;
    let totalCount = 0;
    let hasMore = true;

    while (hasMore) {
      const response = await getUnifiedTransactions({
        ...params,
        page,
        page_size: pageSize,
      });

      const results = response.results || [];
      totalCount = response.total_count || totalCount;

      allResults = [...allResults, ...results];

      // Report progress after first batch
      if (onProgress && page === 1 && results.length > 0) {
        onProgress({
          results: allResults,
          count: allResults.length,
          total_count: totalCount,
          isComplete: false,
        });
      }

      // Check if we have more pages
      hasMore = results.length >= pageSize && allResults.length < totalCount;
      page++;

      // Safety limit to prevent infinite loops
      if (page > 100) {
        hasMore = false;
      }
    }

    return {
      results: allResults,
      count: allResults.length,
      total_count: totalCount,
    };
  } catch (error) {
    console.error("Error fetching all unified transactions:", error);
    throw error;
  }
};

export const getTransactionById = async (transactionId) => {
  try {
    const response = await accountingAPI.get(`transactions/${transactionId}/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching transaction:", error);
    throw error;
  }
};

/**
 * Get all payment allocations for a transaction
 * @param {number} transactionId - The ID of the transaction
 * @returns {Promise<Array>} - The payment allocations
 */
export const getTransactionAllocations = async (transactionId) => {
  try {
    const response = await accountingAPI.get(`payment-allocations/`, {
      params: { transaction: transactionId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching transaction allocations:", error);
    throw error;
  }
};

/**
 * Get all payment allocations for an invoice
 * @param {number} invoiceId - The ID of the invoice
 * @returns {Promise<Array>} - The payment allocations
 */
export const getInvoiceAllocations = async (invoiceId) => {
  try {
    const response = await accountingAPI.get(`payment-allocations/`, {
      params: { invoice: invoiceId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching invoice allocations:", error);
    throw error;
  }
};

export const updateTransaction = async (id, transactionData) => {
  try {
    const response = await accountingAPI.put(
      `transactions/${id}/`,
      transactionData
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating transaction ${id}:`, error);
    throw error;
  }
};

export const allocatePayment = async (transactionId, data) => {
  try {
    // Use the payment allocation endpoint
    const response = await accountingAPI.post(
      `payment-allocations/allocate_payment/`,
      {
        transaction_id: transactionId,
        invoice_id: data.invoice_id,
        amount: data.amount,
        notes: data.notes || "",
        partner_id: data.partner_id || null,
        // Include allocation_id for updates/deallocations
        ...(data.allocation_id ? { allocation_id: data.allocation_id } : {}),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error allocating payment:", error);
    throw error;
  }
};

export const deletePaymentAllocation = async (allocationId) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.delete(
      `payment-allocations/${allocationId}/`
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting payment allocation:", error);
    throw error;
  }
};

export const autoCreateBillFromTransaction = async (
  transactionId,
  partnerId,
  customAmount = null,
  customDescription = null
) => {
  try {
    setAuthHeader();
    const payload = {
      partner_id: partnerId,
    };

    // Add custom amount and description if provided
    if (customAmount !== null && customAmount !== undefined) {
      payload.amount = customAmount;
    }
    if (customDescription) {
      payload.description = customDescription;
    }

    const response = await accountingAPI.post(
      `transactions/${transactionId}/auto-create-bill/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error auto-creating bill from transaction:", error);
    throw error;
  }
};

export const autoCreateInvoiceFromTransaction = async (
  transactionId,
  partnerId,
  customAmount = null,
  customDescription = null
) => {
  try {
    setAuthHeader();
    const payload = {
      partner_id: partnerId,
    };

    // Add custom amount and description if provided
    if (customAmount !== null && customAmount !== undefined) {
      payload.amount = customAmount;
    }
    if (customDescription) {
      payload.description = customDescription;
    }

    const response = await accountingAPI.post(
      `transactions/${transactionId}/auto-create-invoice/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error auto-creating invoice from transaction:", error);
    throw error;
  }
};

// BillsFile API functions
export const getAllBillsFiles = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await accountingAPI.get(`bills-files/?${params}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching bills files:", error);
    throw error;
  }
};

export const getBillsFileById = async (id) => {
  try {
    const response = await accountingAPI.get(`bills-files/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching bills file ${id}:`, error);
    throw error;
  }
};

export const getLatestBillsFile = async (brandId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/accounting/bills-files/latest/`,
      {
        params: { brand: brandId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching latest bills file:", error);
    // Return null instead of throwing to handle 404 gracefully
    if (error.response && error.response.status === 404) {
      return null;
    }
    throw error;
  }
};

export const uploadBillsFile = async (billsFileData, fileObj) => {
  try {
    // Create FormData for file upload
    const formData = new FormData();

    // Add file if provided
    if (fileObj) {
      formData.append("file", fileObj);
    }

    // Add other data fields
    Object.entries(billsFileData).forEach(([key, value]) => {
      if (key !== "file" && value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    // Send the request as multipart/form-data
    const token = localStorage.getItem("accessToken");
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/accounting/bills-files/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading bills file:", error);
    throw error;
  }
};

export const deleteBillsFile = async (id) => {
  try {
    await accountingAPI.delete(`bills-files/${id}/`);
    return true;
  } catch (error) {
    console.error(`Error deleting bills file ${id}:`, error);
    throw error;
  }
};

// Vendor Statistics API function
export const getVendorInvoiceStatistics = async (brands, filters = {}) => {
  try {
    setAuthHeader();

    // Create request body with brands and filters

    // Create request body with brands and filters
    const requestBody = {
      brands: brands,
      ...filters,
    };

    const response = await accountingAPI.post(
      "invoices/vendor-statistics/",
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching vendor statistics:", error);
    throw error;
  }
};

// Vendor Aging Report API function
export const getVendorAgingReport = async (brands, filters = {}) => {
  try {
    setAuthHeader();

    // Create request body with brands and filters
    const requestBody = {
      brands: brands,
      ...filters,
    };

    const response = await accountingAPI.post(
      "invoices/vendor-aging-report/",
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching vendor aging report:", error);
    throw error;
  }
};

// Upcoming Invoices API function - separate endpoint
export const getUpcomingInvoices = async (brands, filters = {}) => {
  try {
    setAuthHeader();

    // Create request body with brands and filters
    const requestBody = {
      brands: brands,
      ...filters,
    };

    const response = await accountingAPI.post(
      "invoices/upcoming-invoices/",
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming invoices:", error);
    throw error;
  }
};

// Credit API Functions
// Credit data cached for 15 seconds (changes frequently)
export const getAllCredits = async (filters = {}) => {
  const params = new URLSearchParams();

  // Add filters
  if (filters.partner) params.append("partner", filters.partner);
  if (filters.brand) params.append("brand", filters.brand);
  if (filters.credit_type) params.append("credit_type", filters.credit_type);
  if (filters.number) params.append("number", filters.number);
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  if (filters.min_amount) params.append("min_amount", filters.min_amount);
  if (filters.max_amount) params.append("max_amount", filters.max_amount);
  if (filters.search) params.append("search", filters.search);
  if (filters.approval_status) params.append("approval_status", filters.approval_status);

  // Add allocation information if requested
  if (filters.include_allocations) params.append("include_allocations", "true");

  try {
    const queryString = params.toString();
    const path = `credits/${queryString ? "?" + queryString : ""}`;
    // Use shorter 15s cache for credits since they change frequently
    const data = await cachedGetData(path, { ttlMs: 15000 });
    return data;
  } catch (error) {
    console.error("Error fetching credits:", error);
    throw error;
  }
};

/**
 * Fetch ALL credits in a single request (optimized for large datasets)
 * Uses a large page_size to minimize HTTP requests and overhead
 * @param {Object} filters - Filter parameters
 * @param {number} maxRecords - Maximum records to fetch (default 50000)
 * @returns {Promise<Object>} - Results with all credits
 */
// Progressive loading: fetch 1000 at a time, call callback with each batch
export const getAllCreditsProgressive = async (
  filters = {},
  onBatch = null
) => {
  const batchSize = 1000; // Fetch 1000 records per batch
  let allResults = [];
  let totalCount = 0;
  let currentPage = 1;
  let hasMore = true;
  let firstBatchDelivered = false;

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();

  // Add filters
  if (filters.partner) baseParams.append("partner", filters.partner);
  if (filters.brand) baseParams.append("brand", filters.brand);
  if (filters.credit_type)
    baseParams.append("credit_type", filters.credit_type);
  if (filters.number) baseParams.append("number", filters.number);
  if (filters.start_date) baseParams.append("start_date", filters.start_date);
  if (filters.end_date) baseParams.append("end_date", filters.end_date);
  if (filters.min_amount) baseParams.append("min_amount", filters.min_amount);
  if (filters.max_amount) baseParams.append("max_amount", filters.max_amount);
  if (filters.search) baseParams.append("search", filters.search);
  if (filters.include_allocations)
    baseParams.append("include_allocations", "true");

  try {
    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(batchSize));

      const path = `credits/?${params.toString()}`;

      const data = await cachedGetData(path, { ttlMs: 30000 });

      const pageResults = data?.results || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Call callback with batch if provided - use setTimeout to allow UI to update
      if (onBatch && typeof onBatch === "function") {
        const isFirst = !firstBatchDelivered;
        if (isFirst) {
          // First batch: call immediately
          onBatch(pageResults, {
            currentPage,
            totalFetched: allResults.length,
            totalCount: totalCount || allResults.length,
            isFirstBatch: true,
          });
          firstBatchDelivered = true;
        } else {
          // Subsequent batches: allow UI to update between batches
          await new Promise((resolve) => setTimeout(resolve, 0));
          onBatch(pageResults, {
            currentPage,
            totalFetched: allResults.length,
            totalCount: totalCount || allResults.length,
            isFirstBatch: false,
          });
        }
      }

      // Check if we have more pages to fetch
      if (pageResults.length === 0) {
        hasMore = false;
      } else if (pageResults.length === batchSize) {
        currentPage++;
        if (currentPage > 1000) {
          hasMore = false;
        }
      } else if (pageResults.length < batchSize) {
        if (allResults.length >= totalCount && totalCount > 0) {
          hasMore = false;
        } else {
          currentPage++;
          if (currentPage > 1000) {
            hasMore = false;
          }
        }
      } else {
        currentPage++;
      }
    }

    return { results: allResults, count: totalCount || allResults.length };
  } catch (error) {
    console.error("Error fetching all credits:", error);
    throw error;
  }
};

export const getAllCreditsParallel = async (filters = {}) => {
  const pageSize = 1000; // Page size for fetching
  setAuthHeader();

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();

  // Add filters
  if (filters.partner) baseParams.append("partner", filters.partner);
  if (filters.brand) baseParams.append("brand", filters.brand);
  if (filters.credit_type)
    baseParams.append("credit_type", filters.credit_type);
  if (filters.number) baseParams.append("number", filters.number);
  if (filters.start_date) baseParams.append("start_date", filters.start_date);
  if (filters.end_date) baseParams.append("end_date", filters.end_date);
  if (filters.min_amount) baseParams.append("min_amount", filters.min_amount);
  if (filters.max_amount) baseParams.append("max_amount", filters.max_amount);
  if (filters.search) baseParams.append("search", filters.search);
  if (filters.include_allocations)
    baseParams.append("include_allocations", "true");

  try {
    // Step 1: Fetch first page to get total count
    const firstPageParams = new URLSearchParams(baseParams);
    firstPageParams.set("page", "1");
    firstPageParams.set("page_size", String(pageSize));
    const firstPagePath = `credits/?${firstPageParams.toString()}`;

    // Use queuedRequest to prevent overwhelming browser connections
    const firstPageData = await queuedRequest(() =>
      accountingAPI.get(firstPagePath)
    );
    const firstPageResults =
      firstPageData?.data?.results ||
      (Array.isArray(firstPageData?.data) ? firstPageData.data : []);
    const totalCount =
      firstPageData?.data?.count || firstPageData?.data?.total || 0;

    // If we got less than a full page, we have all results
    if (firstPageResults.length < pageSize) {
      return {
        results: firstPageResults,
        count: totalCount || firstPageResults.length,
      };
    }

    // Step 2: Calculate total pages needed
    const estimatedTotal =
      totalCount > 0 ? totalCount : firstPageResults.length * 10;
    const totalPages = Math.min(Math.ceil(estimatedTotal / pageSize), 50);

    if (totalPages <= 1) {
      return {
        results: firstPageResults,
        count: totalCount || firstPageResults.length,
      };
    }

    // Step 3: Fetch ALL remaining pages in parallel
    const remainingPages = [];
    for (let page = 2; page <= totalPages; page++) {
      remainingPages.push(page);
    }

    const pagePromises = remainingPages.map(async (pageNum) => {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(pageNum));
      params.set("page_size", String(pageSize));
      const path = `credits/?${params.toString()}`;

      try {
        // Use queuedRequest to prevent overwhelming browser connections
        const response = await queuedRequest(() => accountingAPI.get(path));
        return (
          response?.data?.results ||
          (Array.isArray(response?.data) ? response.data : [])
        );
      } catch (err) {
        console.error(`Error fetching credits page ${pageNum}:`, err);
        return [];
      }
    });

    const pageResults = await Promise.all(pagePromises);

    // Combine all results
    let allResults = [...firstPageResults];
    for (const results of pageResults) {
      if (results.length > 0) {
        allResults = allResults.concat(results);
      }
    }

    return { results: allResults, count: totalCount || allResults.length };
  } catch (error) {
    console.error("Error fetching all credits:", error);
    throw error;
  }
};

export const getCreditById = async (id, include_allocations = false) => {
  setAuthHeader();
  try {
    const params = include_allocations ? "?include_allocations=true" : "";
    const response = await accountingAPI.get(`credits/${id}/${params}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching credit:", error);
    throw error;
  }
};

export const createCreditWithFile = async (formData, version = "") => {
  setAuthHeader();
  const headers = {
    "Content-Type": "multipart/form-data",
  };

  // Use the version parameter to determine which endpoint to use
  const endpoint = version ? `credits/` : "credits/";

  try {
    const response = await accountingAPI.post(endpoint, formData, { headers });
    return response.data;
  } catch (error) {
    console.error("Error creating credit:", error);
    throw error;
  }
};

export const updateCreditWithFile = async (id, formData) => {
  setAuthHeader();
  const headers = {
    "Content-Type": "multipart/form-data",
  };

  try {
    const response = await accountingAPI.put(`credits/${id}/`, formData, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating credit:", error);
    throw error;
  }
};

export const deleteCredit = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.delete(`credits/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Error deleting credit:", error);
    throw error;
  }
};

export const approveCredit = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(`credits/${id}/approve/`);
    return response.data;
  } catch (error) {
    console.error(`Error approving credit ${id}:`, error);
    throw error;
  }
};

export const rejectCredit = async (id, rejectionReason = "") => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(`credits/${id}/reject/`, {
      rejection_reason: rejectionReason,
    });
    return response.data;
  } catch (error) {
    console.error(`Error rejecting credit ${id}:`, error);
    throw error;
  }
};

export const splitCredit = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(`credits/${id}/split/`);
    return response.data;
  } catch (error) {
    console.error(`Error splitting credit ${id}:`, error);
    throw error;
  }
};

export const getCreditHistory = async (creditId) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.get(`credits/${creditId}/history/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching credit history:", error);
    throw error;
  }
};

export const rollbackCreditHistory = async (historyId, recordDate) => {
  setAuthHeader();
  const response = await accountingAPI.post(`credits/rollback/`, {
    history_id: historyId,
    record_date: recordDate,
  });
  return response.data;
};

export const getPartnerCreditStatistics = async (brands, filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();

  // Add brand filter
  if (brands && brands.length > 0) {
    brands.forEach((brand) => params.append("brands", brand));
  }

  // Add other filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });

  return accountingAPI.get(`credits/partner-statistics/?${params.toString()}`);
};

// Credit Allocation API Functions
export const getAllCreditAllocations = async (filters = {}) => {
  setAuthHeader();
  const pageSize = 1000; // Page size for fetching
  let allResults = [];
  let totalCount = 0;
  let currentPage = 1;
  let hasMore = true;

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();
  if (filters.credit) baseParams.append("credit", filters.credit);
  if (filters.invoice) baseParams.append("invoice", filters.invoice);
  if (filters.brand) baseParams.append("brand", filters.brand);

  // Fetch all pages until we have all records
  while (hasMore) {
    const params = new URLSearchParams(baseParams);
    params.set("page", String(currentPage));
    params.set("page_size", String(pageSize));

    const response = await accountingAPI.get(
      `credit-allocations/${params.toString() ? "?" + params.toString() : ""}`
    );
    const data = response?.data || {};

    const pageResults =
      data?.results || data?.data || (Array.isArray(data) ? data : []);
    const pageCount = data?.count || 0;

    // Update total count from first page
    if (currentPage === 1) {
      totalCount = pageCount;
    }

    // Add results from this page
    allResults = [...allResults, ...pageResults];

    // Check if we have more pages to fetch
    if (totalCount === 0 && currentPage === 1) {
      // Backend doesn't support pagination - we got all results
      hasMore = false;
    } else if (
      pageResults.length < pageSize ||
      allResults.length >= totalCount
    ) {
      // We've fetched all pages
      hasMore = false;
    } else {
      currentPage++;
    }
  }

  return {
    results: allResults,
    count: totalCount || allResults.length,
    data: allResults,
  };
};

export const getCreditAllocationsForCredit = async (creditId) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.get(
      `credits/${creditId}/allocations/`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching credit allocations:", error);
    throw error;
  }
};

export const createCreditAllocation = async (allocationData) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(
      "credit-allocations/",
      allocationData
    );
    return response.data;
  } catch (error) {
    console.error("Error creating credit allocation:", error);
    throw error;
  }
};

export const updateCreditAllocation = async (id, allocationData) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.put(
      `credit-allocations/${id}/`,
      allocationData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating credit allocation:", error);
    throw error;
  }
};

export const deleteCreditAllocation = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.delete(`credit-allocations/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Error deleting credit allocation:", error);
    throw error;
  }
};

export const allocateCredit = async (creditId, data) => {
  setAuthHeader();
  try {
    // Use the credit allocation endpoint
    const response = await accountingAPI.post(
      `credit-allocations/allocate_credit/`,
      {
        credit_id: creditId,
        invoice_id: data.invoice_id,
        amount: data.amount,
        notes: data.notes || "",
        partner_id: data.partner_id || null,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error allocating credit:", error);
    throw error;
  }
};

export const getUncreditedInvoices = async (filters = {}) => {
  setAuthHeader();
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.brand) baseParams.append("brand", filters.brand);
    if (filters.partner) baseParams.append("partner", filters.partner);
    if (filters.credit) baseParams.append("credit", filters.credit);

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const response = await accountingAPI.get(
        `credits/${filters.credit}/uncredited-invoices/?${params.toString()}`
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching uncredited invoices:", error);
    throw error;
  }
};

// Cash API Functions
export const getAllCash = async (filters = {}) => {
  setAuthHeader();
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.search) baseParams.append("search", filters.search);
    if (filters.partner) baseParams.append("partner", filters.partner);
    if (filters.brand) baseParams.append("brand", filters.brand);
    if (filters.cash_type) baseParams.append("cash_type", filters.cash_type);
    if (filters.start_date) baseParams.append("start_date", filters.start_date);
    if (filters.end_date) baseParams.append("end_date", filters.end_date);
    baseParams.append("include_allocations", "true");

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const url = `cash/?${params.toString()}`;
      const data = await cachedGetData(url, { ttlMs: 30 * 1000 });

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching cash payments:", error);
    throw error;
  }
};

export const getCashById = async (id, include_allocations = false) => {
  setAuthHeader();
  try {
    const params = new URLSearchParams();
    if (include_allocations) params.append("include_allocations", "true");

    const queryString = params.toString();
    const response = await accountingAPI.get(
      `cash/${id}/${queryString ? "?" + queryString : ""}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching cash payment:", error);
    throw error;
  }
};

export const createCashWithFile = async (formData) => {
  setAuthHeader();
  try {
    const headers = {
      "Content-Type": "multipart/form-data",
    };

    const response = await accountingAPI.post("cash/", formData, { headers });
    return response.data;
  } catch (error) {
    console.error("Error creating cash payment:", error);
    throw error;
  }
};

export const updateCashWithFile = async (id, formData) => {
  setAuthHeader();
  try {
    const headers = {
      "Content-Type": "multipart/form-data",
    };

    const response = await accountingAPI.put(`cash/${id}/`, formData, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating cash payment:", error);
    throw error;
  }
};

export const deleteCash = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.delete(`cash/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Error deleting cash payment:", error);
    throw error;
  }
};

export const getCashHistory = async (cashId) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.get(`cash/${cashId}/history/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching cash payment history:", error);
    throw error;
  }
};

export const getPartnerCashStatistics = async (brands, filters = {}) => {
  setAuthHeader();
  try {
    const params = new URLSearchParams();

    // Add brand filter
    if (brands && brands.length > 0) {
      brands.forEach((brand) => params.append("brands", brand));
    }

    // Add other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      }
    });

    const response = await accountingAPI.get(
      `cash/partner-statistics/?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching partner cash statistics:", error);
    throw error;
  }
};

// Cash Allocation API Functions
export const getAllCashAllocations = async (filters = {}) => {
  setAuthHeader();
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.cash) baseParams.append("cash", filters.cash);
    if (filters.invoice) baseParams.append("invoice", filters.invoice);
    if (filters.brand) baseParams.append("brand", filters.brand);

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const queryString = params.toString();
      const response = await accountingAPI.get(
        `cash-allocations/${queryString ? "?" + queryString : ""}`
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching cash allocations:", error);
    throw error;
  }
};

export const getCashAllocationsForCash = async (cashId) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.get(`cash/${cashId}/allocations/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching cash allocations:", error);
    throw error;
  }
};

export const createCashAllocation = async (allocationData) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.post(
      "cash-allocations/",
      allocationData
    );
    return response.data;
  } catch (error) {
    console.error("Error creating cash allocation:", error);
    throw error;
  }
};

export const updateCashAllocation = async (id, allocationData) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.put(
      `cash-allocations/${id}/`,
      allocationData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating cash allocation:", error);
    throw error;
  }
};

export const deleteCashAllocation = async (id) => {
  setAuthHeader();
  try {
    const response = await accountingAPI.delete(`cash-allocations/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Error deleting cash allocation:", error);
    throw error;
  }
};

export const allocateCash = async (cashId, data) => {
  setAuthHeader();
  try {
    // Use the cash allocation endpoint
    const response = await accountingAPI.post(
      `cash-allocations/allocate_cash/`,
      {
        cash_id: cashId,
        invoice_id: data.invoice_id,
        amount: data.amount,
        notes: data.notes || "",
        partner_id: data.partner_id || null,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error allocating cash payment:", error);
    throw error;
  }
};

export const getUnpaidInvoicesForCash = async (filters = {}) => {
  setAuthHeader();
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.brand) baseParams.append("brand", filters.brand);
    if (filters.partner) baseParams.append("partner", filters.partner);
    if (filters.cash) baseParams.append("cash", filters.cash);

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const response = await accountingAPI.get(
        `cash/${filters.cash}/unpaid-invoices/?${params.toString()}`
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching unpaid invoices for cash:", error);
    throw error;
  }
};

export const getCashPartnerInvoices = async (params = {}) => {
  try {
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = { ...params };
    delete baseParams.page;
    delete baseParams.page_size;

    // Fetch all pages until we have all records
    while (hasMore) {
      const requestParams = {
        ...baseParams,
        page: currentPage,
        page_size: pageSize,
      };

      const response = await accountingAPI.get(
        `cash-allocations/partner_invoices/`,
        {
          params: requestParams,
        }
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching cash partner invoices:", error);
    throw error;
  }
};

// Partner Overpayment API Functions
export const getAllPartnerOverpayments = async (filters = {}) => {
  try {
    setAuthHeader();
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.brand) baseParams.append("brand", filters.brand);
    if (filters.partner) baseParams.append("partner", filters.partner);
    if (filters.transaction)
      baseParams.append("transaction", filters.transaction);
    if (filters.has_remaining)
      baseParams.append("has_remaining", filters.has_remaining);
    if (filters.include_allocations)
      baseParams.append("include_allocations", filters.include_allocations);
    if (filters.start_date) baseParams.append("start_date", filters.start_date);
    if (filters.end_date) baseParams.append("end_date", filters.end_date);

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const queryString = params.toString();
      const url = `partner-overpayments/${
        queryString ? "?" + queryString : ""
      }`;

      const data = await cachedGetData(url, { ttlMs: 30 * 1000 });

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching partner overpayments:", error);
    throw error;
  }
};

export const getPartnerOverpaymentById = async (id) => {
  try {
    setAuthHeader();
    const response = await accountingAPI.get(`partner-overpayments/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching partner overpayment ${id}:`, error);
    throw error;
  }
};

export const createOverpaymentFromTransaction = async (data) => {
  try {
    setAuthHeader();
    const response = await accountingAPI.post(
      "partner-overpayments/create_from_transaction/",
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error creating overpayment from transaction:", error);
    throw error;
  }
};

// Overpayment Allocation API Functions
export const getAllOverpaymentAllocations = async (filters = {}) => {
  try {
    setAuthHeader();
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.overpayment)
      baseParams.append("overpayment", filters.overpayment);
    if (filters.invoice) baseParams.append("invoice", filters.invoice);
    if (filters.brand) baseParams.append("brand", filters.brand);

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const response = await accountingAPI.get(
        `overpayment-allocations/?${params}`
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching overpayment allocations:", error);
    throw error;
  }
};

// In-flight tracking for overpayment details to prevent duplicate requests
const overpaymentDetailsInFlight = new Map();

export const getOverpaymentDetails = async (overpaymentId) => {
  // Prevent duplicate concurrent requests for the same overpayment
  if (overpaymentDetailsInFlight.has(overpaymentId)) {
    return overpaymentDetailsInFlight.get(overpaymentId);
  }
  
  const requestPromise = (async () => {
    try {
      setAuthHeader();
      const response = await accountingAPI.get(
        `overpayment-allocations/get_overpayment_details/?overpayment_id=${overpaymentId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching overpayment details for ${overpaymentId}:`,
        error
      );
      throw error;
    } finally {
      // Clean up after a short delay
      setTimeout(() => overpaymentDetailsInFlight.delete(overpaymentId), 100);
    }
  })();
  
  overpaymentDetailsInFlight.set(overpaymentId, requestPromise);
  return requestPromise;
};

export const getUnpaidInvoicesForOverpayment = async (filters = {}) => {
  try {
    setAuthHeader();
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.brand) baseParams.append("brand", filters.brand);
    if (filters.partner_id) baseParams.append("partner_id", filters.partner_id);
    if (filters.overpayment_id)
      baseParams.append("overpayment_id", filters.overpayment_id);

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const response = await accountingAPI.get(
        `overpayment-allocations/unpaid_invoices/?${params}`
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching unpaid invoices for overpayment:", error);
    throw error;
  }
};

export const getOtherOverpaymentsForAllocation = async (filters = {}) => {
  try {
    setAuthHeader();
    const pageSize = 1000; // Page size for fetching
    let allResults = [];
    let totalCount = 0;
    let currentPage = 1;
    let hasMore = true;

    // Build base params (without pagination)
    const baseParams = new URLSearchParams();
    if (filters.brand) baseParams.append("brand", filters.brand);
    if (filters.partner_id) baseParams.append("partner_id", filters.partner_id);
    if (filters.overpayment_id)
      baseParams.append("overpayment_id", filters.overpayment_id);
    if (filters.has_remaining)
      baseParams.append("has_remaining", filters.has_remaining);

    // Fetch all pages until we have all records
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(pageSize));

      const response = await accountingAPI.get(
        `overpayment-allocations/get_other_overpayments_for_allocation/?${params}`
      );
      const data = response?.data || {};

      const pageResults =
        data?.results || data?.data || (Array.isArray(data) ? data : []);
      const pageCount = data?.count || 0;

      // Update total count from first page
      if (currentPage === 1) {
        totalCount = pageCount;
      }

      // Add results from this page
      allResults = [...allResults, ...pageResults];

      // Check if we have more pages to fetch
      if (totalCount === 0 && currentPage === 1) {
        // Backend doesn't support pagination - we got all results
        hasMore = false;
      } else if (
        pageResults.length < pageSize ||
        allResults.length >= totalCount
      ) {
        // We've fetched all pages
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return {
      results: allResults,
      count: totalCount || allResults.length,
      data: allResults,
    };
  } catch (error) {
    console.error("Error fetching other overpayments for allocation:", error);
    throw error;
  }
};

export const allocateOverpayment = async (overpaymentId, data) => {
  try {
    setAuthHeader();

    // Determine which endpoint to use based on whether we're allocating to an invoice or another overpayment
    const endpoint = data.target_overpayment_id
      ? "overpayment-allocations/allocate_to_overpayment/"
      : "overpayment-allocations/allocate_overpayment/";

    const response = await accountingAPI.post(endpoint, {
      overpayment_id: overpaymentId,
      ...data,
    });
    return response.data;
  } catch (error) {
    console.error("Error allocating/unallocating overpayment:", error);
    throw error;
  }
};

// We no longer use a separate deallocateOverpayment function
// All unallocations are now handled through allocateOverpayment with amount=0

//Profit and loss

export const getProfitAndLoss = async (filters = {}) => {
  try {
    setAuthHeader();
    const params = new URLSearchParams();

    // Date range
    if (filters.dateRange && Array.isArray(filters.dateRange)) {
      if (filters.dateRange[0]) params.append("start", filters.dateRange[0]);
      if (filters.dateRange[1]) params.append("end", filters.dateRange[1]);
    }

    if (filters.brandIds && Array.isArray(filters.brandIds)) {
      filters.brandIds.forEach((id) => params.append("brand_ids[]", id));
    }

    if (
      filters.clientIds &&
      Array.isArray(filters.clientIds) &&
      filters.clientIds.length > 0
    ) {
      params.append("client_ids", filters.clientIds.join(","));
    }

    // Other filters (future use)
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value &&
        !["dateRange", "brandIds", "brand_ids", "clientIds"].includes(key)
      ) {
        params.append(key, value);
      }
    });

    const response = await accountingAPI.get(
      `/api/pnl/${params.toString() ? "?" + params.toString() : ""}`
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching Profit & Loss:", error);
    throw error;
  }
};

//Journal Entry

export const fetchJournalEntries = async (filters = {}) => {
  setAuthHeader();
  const pageSize = 200; // Smaller page size for faster initial load
  let allResults = [];
  let totalCount = 0;
  let currentPage = 1;
  let hasMore = true;

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();
  if (filters.start) baseParams.append("start", filters.start);
  if (filters.end) baseParams.append("end", filters.end);
  if (filters.brandIds && filters.brandIds.length > 0) {
    filters.brandIds.forEach((id) => baseParams.append("brand_ids[]", id));
  }

  // Fetch all pages until we have all records
  while (hasMore) {
    const params = new URLSearchParams(baseParams);
    params.set("page", String(currentPage));
    params.set("page_size", String(pageSize));

    const response = await accountingAPI.get(`/api/journal/?${params}`);
    const data = response?.data || {};

    const pageResults =
      data?.results || data?.data || (Array.isArray(data) ? data : []);
    const pageCount = data?.count || 0;

    // Update total count from first page
    if (currentPage === 1) {
      totalCount = pageCount;
    }

    // Add results from this page
    allResults = [...allResults, ...pageResults];

    // Check if we have more pages to fetch
    // If backend doesn't support pagination (no count field), assume first page has all results
    if (totalCount === 0 && currentPage === 1) {
      // Backend doesn't support pagination - we got all results
      hasMore = false;
    } else if (
      pageResults.length < pageSize ||
      allResults.length >= totalCount
    ) {
      // We've fetched all pages
      hasMore = false;
    } else {
      currentPage++;
    }
  }

  return {
    results: allResults,
    count: totalCount || allResults.length,
    data: allResults,
  };
};

//Balance Sheet

export const fetchBalanceSheet = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (filters.start) params.append("start", filters.start);
  if (filters.end) params.append("end", filters.end);

  if (filters.brandIds && filters.brandIds.length > 0) {
    // Backend expects comma-separated brand_id parameter - Giofix
    params.append("brand_id", filters.brandIds.join(","));
  }

  if (
    filters.clientIds &&
    Array.isArray(filters.clientIds) &&
    filters.clientIds.length > 0
  ) {
    params.append("client_ids", filters.clientIds.join(","));
  }

  if (filters.date_filter) params.append("date_filter", filters.date_filter);

  const response = await accountingAPI.get(`/api/balance-sheet/?${params}`);
  return response.data;
};

// Letter Processing API Functions
export const getLetterProcessing = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();

  // Add all filters to params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  return await accountingAPI.get(
    `letter-processing/${queryString ? "?" + queryString : ""}`
  );
};

export const getLetterProcessingById = async (id) => {
  setAuthHeader();
  return await accountingAPI.get(`letter-processing/${id}/`);
};

export const createLetterProcessing = async (data) => {
  setAuthHeader();
  return await accountingAPI.post("letter-processing/", data);
};

export const updateLetterProcessing = async (id, data) => {
  setAuthHeader();
  return await accountingAPI.put(`letter-processing/${id}/`, data);
};

export const deleteLetterProcessing = async (id) => {
  setAuthHeader();
  return await accountingAPI.delete(`letter-processing/${id}/`);
};

export const getLetterProcessingSummary = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();

  // Add all filters to params
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  return await accountingAPI.get(
    `letter-processing/summary/${queryString ? "?" + queryString : ""}`
  );
};

// Manual Journal Entry API Functions
export const fetchManualJournalEntries = async (filters = {}) => {
  setAuthHeader();
  const pageSize = 1000; // Page size for fetching
  let allResults = [];
  let totalCount = 0;
  let currentPage = 1;
  let hasMore = true;

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();
  if (filters.start) baseParams.append("start", filters.start);
  if (filters.end) baseParams.append("end", filters.end);
  if (filters.brandIds && filters.brandIds.length > 0) {
    filters.brandIds.forEach((id) => baseParams.append("brand_ids[]", id));
  }

  // Fetch all pages until we have all records
  while (hasMore) {
    const params = new URLSearchParams(baseParams);
    params.set("page", String(currentPage));
    params.set("page_size", String(pageSize));

    const response = await accountingAPI.get(
      `/manual-journal-entries/?${params}`
    );
    const data = response?.data || {};

    const pageResults =
      data?.results || data?.data || (Array.isArray(data) ? data : []);
    const pageCount = data?.count || 0;

    // Update total count from first page
    if (currentPage === 1) {
      totalCount = pageCount;
    }

    // Add results from this page
    allResults = [...allResults, ...pageResults];

    // Check if we have more pages to fetch
    // If backend doesn't support pagination (no count field), assume first page has all results
    if (totalCount === 0 && currentPage === 1) {
      // Backend doesn't support pagination - we got all results
      hasMore = false;
    } else if (
      pageResults.length < pageSize ||
      allResults.length >= totalCount
    ) {
      // We've fetched all pages
      hasMore = false;
    } else {
      currentPage++;
    }
  }

  return {
    results: allResults,
    count: totalCount || allResults.length,
    data: allResults,
  };
};

export const createManualJournalEntry = async (data) => {
  setAuthHeader();
  const response = await accountingAPI.post("/manual-journal-entries/", data);
  return response.data;
};

export const updateManualJournalEntry = async (id, data) => {
  setAuthHeader();
  const response = await accountingAPI.put(
    `/manual-journal-entries/${id}/`,
    data
  );
  return response.data;
};

export const deleteManualJournalEntry = async (id) => {
  setAuthHeader();
  const response = await accountingAPI.delete(`/manual-journal-entries/${id}/`);
  return response.data;
};

export const getJournalEntryBatch = async (entryId) => {
  setAuthHeader();
  const response = await accountingAPI.get(
    `/manual-journal-entries/${entryId}/batch-entries/`
  );
  return response.data;
};

// Account API Functions
export const getAllAccounts = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();

  // Add filters if needed
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  const response = await accountingAPI.get(
    `accounts/${queryString ? "?" + queryString : ""}`
  );
  return response.data;
};

// Fetch nested account tree
// CACHED (5 min TTL) - Account tree rarely changes
export const getAccountTree = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const queryString = params.toString();
  const url = `accounts/tree/${queryString ? "?" + queryString : ""}`;

  // Use 5 minute cache for account tree (rarely changes)
  return await cachedGetData(url, { ttlMs: 5 * 60 * 1000 });
};

/** Full chart for pickers (batch edit, etc.): no GET cache, always full=1 so brand-scoped tree is never used. */
export const getAccountTreeFullFresh = async () => {
  setAuthHeader();
  const response = await accountingAPI.get("accounts/tree/?full=1");
  return response.data;
};

// Fetch account balances
export const getAccountBalances = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const queryString = params.toString();
  const url = queryString
    ? `accounts/balances/?${queryString}`
    : `accounts/balances/`;
  const response = await accountingAPI.get(url);
  // Backend returns a dictionary with account IDs as keys (may be strings or numbers)
  // Convert to a normalized format where keys are stored as both numbers and strings for flexible lookup
  const balances = response.data || {};
  const normalizedBalances = {};
  Object.keys(balances).forEach((key) => {
    const accountId = parseInt(key, 10);
    if (!isNaN(accountId)) {
      // Store as both number and string key for maximum compatibility
      normalizedBalances[accountId] = balances[key];
      normalizedBalances[String(accountId)] = balances[key];
    }
  });
  return normalizedBalances;
};

// Create a new account (category/group or postable leaf)
export const createAccount = async (data) => {
  setAuthHeader();
  const response = await accountingAPI.post("accounts/", data);
  return response.data;
};

// Update an existing account
export const updateAccount = async (id, data) => {
  setAuthHeader();
  const response = await accountingAPI.patch(`accounts/${id}/`, data);
  return response.data;
};

// Delete an account
export const deleteAccount = async (id) => {
  setAuthHeader();
  const response = await accountingAPI.delete(`accounts/${id}/`);
  return response.data;
};

// Mailing Offers API Functions
export const getMailingOffers = async (filters = {}) => {
  try {
    setAuthHeader();

    // Create params for brand filter
    const params = new URLSearchParams();
    if (filters.brand) params.append("brand_id", filters.brand);

    // Use the marketing API endpoint for offers by brand
    const response = await axios.get(
      `${
        process.env.REACT_APP_API_BASE_URL
      }/marketing/api/offers/by-brand/?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching mailing offers:", error);
    throw error;
  }
};

// Envelopes API Functions
export const getEnvelopes = async (filters = {}) => {
  try {
    setAuthHeader();

    // Create params for filters
    const params = new URLSearchParams();
    if (filters.brand) params.append("brand", filters.brand);

    // Use the marketing API endpoint for outside envelopes
    const response = await axios.get(
      `${
        process.env.REACT_APP_API_BASE_URL
      }/marketing/api/outside-envelopes/?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching envelopes:", error);
    throw error;
  }
};

// Giofix
// History API Functions
export const getTransactionHistory = async (filters = {}) => {
  setAuthHeader();
  // Extract pagination params
  const page = filters.page || 1;
  const pageSize = filters.page_size || 50;
  const fetchAll = filters.fetchAll === true; // Only fetch all if explicitly requested
  const limit = filters.limit;
  delete filters.page;
  delete filters.page_size;
  delete filters.fetchAll;
  delete filters.limit;

  // If fetchAll is false or not set, only fetch the requested page
  if (!fetchAll) {
    const baseParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        key !== "page" &&
        key !== "page_size"
      ) {
        baseParams.append(key, value);
      }
    });
    baseParams.set("page", String(page));
    baseParams.set("page_size", String(pageSize));

    const queryString = baseParams.toString();
    const url = `transaction-history/${queryString ? "?" + queryString : ""}`;
    // Use shorter cache TTL for history to ensure fresh data
    const data = await cachedGetData(url, { ttlMs: 10 * 1000 });

    const pageResults =
      data?.results || data?.data?.results || (Array.isArray(data) ? data : []);
    const pageCount = data?.count || data?.data?.count || 0;

    return {
      results: pageResults,
      count: pageCount,
      next: data?.next || false,
      previous: data?.previous || false,
    };
  }

  // Legacy: Fetch all pages (only if fetchAll=true)
  let allResults = [];
  let totalCount = 0;
  let currentPage = 1;
  let hasMore = true;
  const fetchPageSize = limit || 1000;

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      key !== "page" &&
      key !== "page_size"
    ) {
      baseParams.append(key, value);
    }
  });

  // Fetch pages until we have all records OR reach the limit
  while (hasMore) {
    const params = new URLSearchParams(baseParams);
    params.set("page", String(currentPage));
    params.set("page_size", String(fetchPageSize));

    const queryString = params.toString();
    const url = `transaction-history/${queryString ? "?" + queryString : ""}`;

    const data = await cachedGetData(url, { ttlMs: 30 * 1000 });

    const pageResults =
      data?.results || data?.data?.results || (Array.isArray(data) ? data : []);
    const pageCount = data?.count || data?.data?.count || 0;

    // Update total count from first page
    if (currentPage === 1) {
      totalCount = pageCount;
    }

    // If limit is set, only take what we need
    if (limit && allResults.length + pageResults.length > limit) {
      const remaining = limit - allResults.length;
      allResults = [...allResults, ...pageResults.slice(0, remaining)];
      hasMore = false;
    } else {
      allResults = [...allResults, ...pageResults];
    }

    // Check if we have more pages to fetch
    if (limit && allResults.length >= limit) {
      hasMore = false;
    } else if (
      pageResults.length < fetchPageSize ||
      allResults.length >= totalCount
    ) {
      hasMore = false;
    } else {
      currentPage++;
    }
  }

  return { results: allResults, count: totalCount || allResults.length };
};

export const rollbackTransaction = async (historyId, recordDate) => {
  setAuthHeader();
  const response = await accountingAPI.post(`transaction-rollback-history/`, {
    history_id: historyId,
    record_date: recordDate,
  });
  return response.data;
};

export const getPaymentAllocationHistory = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const response = await accountingAPI.get(
    `payment-allocation-history/?${params}`
  );
  return response.data;
};

export const rollbackPaymentAllocation = async (historyId, recordDate) => {
  setAuthHeader();
  const response = await accountingAPI.post(
    `payment-allocation-history/rollback/`,
    {
      history_id: historyId,
      record_date: recordDate,
    }
  );
  return response.data;
};

export const getCreditAllocationHistory = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const response = await accountingAPI.get(
    `credit-allocation-history/?${params}`
  );
  return response.data;
};

export const rollbackCreditAllocation = async (historyId, recordDate) => {
  setAuthHeader();
  const response = await accountingAPI.post(
    `credit-allocation-history/rollback/`,
    {
      history_id: historyId,
      record_date: recordDate,
    }
  );
  return response.data;
};

export const getCashAllocationHistory = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const response = await accountingAPI.get(
    `cash-allocation-history/?${params}`
  );
  return response.data;
};

export const rollbackCashAllocation = async (historyId, recordDate) => {
  setAuthHeader();
  const response = await accountingAPI.post(
    `cash-allocation-history/rollback/`,
    {
      history_id: historyId,
      record_date: recordDate,
    }
  );
  return response.data;
};

export const getOverpaymentAllocationHistory = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const response = await accountingAPI.get(
    `overpayment-allocation-history/?${params}`
  );
  return response.data;
};

export const rollbackOverpaymentAllocation = async (historyId, recordDate) => {
  setAuthHeader();
  const response = await accountingAPI.post(
    `overpayment-allocation-history/rollback/`,
    {
      history_id: historyId,
      record_date: recordDate,
    }
  );
  return response.data;
};

export const getAllStatementHistory = async (filters = {}) => {
  setAuthHeader();
  const pageSize = 1000; // Page size for fetching
  let allResults = [];
  let totalCount = 0;
  let currentPage = 1;
  let hasMore = true;

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();

  // Handle brand as array or single value
  if (
    filters.brand !== undefined &&
    filters.brand !== null &&
    filters.brand !== ""
  ) {
    if (Array.isArray(filters.brand)) {
      filters.brand.forEach((id) => baseParams.append("brand", id));
    } else {
      baseParams.append("brand", filters.brand);
    }
  }

  // Add other filters (exclude pagination)
  Object.entries(filters).forEach(([key, value]) => {
    if (
      key !== "brand" &&
      key !== "page" &&
      key !== "page_size" &&
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      baseParams.append(key, value);
    }
  });

  // Fetch all pages until we have all records
  while (hasMore) {
    const params = new URLSearchParams(baseParams);
    params.set("page", String(currentPage));
    params.set("page_size", String(pageSize));

    const response = await accountingAPI.get(
      `statements/all-history/?${params}`
    );
    const data = response?.data || {};

    const pageResults =
      data?.results || data?.data || (Array.isArray(data) ? data : []);
    const pageCount = data?.count || 0;

    // Update total count from first page
    if (currentPage === 1) {
      totalCount = pageCount;
    }

    // Add results from this page
    allResults = [...allResults, ...pageResults];

    // Check if we have more pages to fetch
    if (totalCount === 0 && currentPage === 1) {
      // Backend doesn't support pagination - we got all results
      hasMore = false;
    } else if (
      pageResults.length < pageSize ||
      allResults.length >= totalCount
    ) {
      // We've fetched all pages
      hasMore = false;
    } else {
      currentPage++;
    }
  }

  return {
    results: allResults,
    count: totalCount || allResults.length,
    data: allResults,
  };
};

export const rollbackStatementHistory = async (historyId, recordDate) => {
  setAuthHeader();
  const response = await accountingAPI.post(`statements/rollback-history/`, {
    history_id: historyId,
    record_date: recordDate,
  });
  return response.data;
};

export const getAllInvoiceItems = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const response = await accountingAPI.get(`invoice-items/?${params}`);
  return response.data;
};

export const getAllInvoiceHistory = async (filters = {}) => {
  setAuthHeader();

  // Build params
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      // Handle brand as array for multiple brand IDs
      if (key === "brand" && typeof value === "string" && value.includes(",")) {
        value.split(",").forEach((id) => {
          if (id.trim()) params.append("brand", id.trim());
        });
      } else {
        params.append(key, value);
      }
    }
  });

  // Set default page size if not provided
  if (!params.has("page")) params.set("page", "1");
  if (!params.has("page_size")) params.set("page_size", "500");

  // Use low priority queue - history is secondary to main data
  const response = await queuedRequest(
    () => accountingAPI.get(`invoices/all-history/?${params}`),
    "low"
  );
  const data = response?.data || {};

  const results =
    data?.results || data?.data || (Array.isArray(data) ? data : []);
  const count = data?.count || results.length;

  return { results, count, data: results };
};

export const rollbackInvoiceHistory = async (historyId) => {
  setAuthHeader();
  const response = await accountingAPI.post(`invoices/rollback-history/`, {
    history_id: historyId,
  });
  return response.data;
};

export const getAllCreditHistory = async (filters = {}) => {
  setAuthHeader();

  // Build params
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      // Handle brand as array for multiple brand IDs
      if (key === "brand" && typeof value === "string" && value.includes(",")) {
        value.split(",").forEach((id) => {
          if (id.trim()) params.append("brand", id.trim());
        });
      } else {
        params.append(key, value);
      }
    }
  });

  // Set default page size if not provided
  if (!params.has("page")) params.set("page", "1");
  if (!params.has("page_size")) params.set("page_size", "500");

  // Use low priority queue - history is secondary to main data
  const response = await queuedRequest(
    () => accountingAPI.get(`credits/all-history/?${params}`),
    "low"
  );
  const data = response?.data || {};

  const results =
    data?.results || data?.data || (Array.isArray(data) ? data : []);
  const count = data?.count || results.length;

  return { results, count, data: results };
};

// Team Comments API Functions
export const getTeamComments = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const queryString = params.toString();
  return await accountingAPI.get(
    `team-comments/${queryString ? "?" + queryString : ""}`
  );
};

export const getTeamCommentById = async (commentId) => {
  setAuthHeader();
  return await accountingAPI.get(`team-comments/${commentId}/`);
};

export const createTeamComment = async (commentData) => {
  setAuthHeader();
  return await accountingAPI.post("team-comments/", commentData);
};

export const updateTeamComment = async (commentId, commentData) => {
  setAuthHeader();
  return await accountingAPI.put(`team-comments/${commentId}/`, commentData);
};

export const deleteTeamComment = async (commentId) => {
  setAuthHeader();
  return await accountingAPI.delete(`team-comments/${commentId}/`);
};

export const getRecentTeamComments = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const queryString = params.toString();
  return await accountingAPI.get(
    `team-comments/recent/${queryString ? "?" + queryString : ""}`
  );
};

export const getTeamCommentStats = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  const queryString = params.toString();
  return await accountingAPI.get(
    `team-comments/stats/${queryString ? "?" + queryString : ""}`
  );
};

// Upcoming Payment Priorities API Functions
export const getUpcomingPaymentPriorities = async (brandIds) => {
  setAuthHeader();
  try {
    const brands = Array.isArray(brandIds) ? brandIds.join(",") : brandIds;
    const response = await accountingAPI.get(
      `upcoming-payment-priorities/list/?brands=${brands}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming payment priorities:", error);
    throw error;
  }
};

export const setUpcomingPaymentPriority = async (
  invoiceId,
  brandId,
  isPriority,
  compositeId = null
) => {
  setAuthHeader();

  try {
    const payload = {
      brand_id: brandId,
      is_priority: isPriority,
    };

    // Use composite_id if provided (for payment term-specific priority), otherwise use invoice_id
    if (compositeId) {
      payload.composite_id = compositeId;
    } else {
      payload.invoice_id = invoiceId;
    }

    const response = await accountingAPI.post(
      "upcoming-payment-priorities/set/",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error setting upcoming payment priority:", error);
    throw error;
  }
};

// Dashboard statistics (cached to avoid duplicate calls from multiple widgets)
export const getDashboardStatistics = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.brand_ids && Array.isArray(filters.brand_ids)) {
    params.append("brand_ids", filters.brand_ids.join(","));
  }
  if (
    filters.client_ids &&
    Array.isArray(filters.client_ids) &&
    filters.client_ids.length > 0
  ) {
    params.append("client_ids", filters.client_ids.join(","));
  }
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);

  try {
    const path = `api/reports-dashboard-stats/?${params.toString()}`;
    return await cachedGetData(path);
  } catch (error) {
    console.error("Error fetching dashboard statistics:", error);
    throw error;
  }
};

export const getAccountsReceivableStatistics = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.brand_ids && Array.isArray(filters.brand_ids)) {
    params.append("brand_ids", filters.brand_ids.join(","));
  }
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);

  try {
    const path = `api/ar-dashboard-stats/?${params.toString()}`;
    return await cachedGetData(path);
  } catch (error) {
    console.error("Error fetching AR dashboard statistics:", error);
    throw error;
  }
};

export const getAccountsPayableStatistics = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.brand_ids && Array.isArray(filters.brand_ids)) {
    params.append("brand_ids", filters.brand_ids.join(","));
  }
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);

  try {
    const path = `api/ap-dashboard-stats/?${params.toString()}`;
    return await cachedGetData(path);
  } catch (error) {
    console.error("Error fetching AP dashboard statistics:", error);
    throw error;
  }
};

export const getTransactionsAllocationsStatistics = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.brand_ids && Array.isArray(filters.brand_ids)) {
    params.append("brand_ids", filters.brand_ids.join(","));
  }
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);

  try {
    const path = `api/transactions-allocations-stats/?${params.toString()}`;
    return await cachedGetData(path);
  } catch (error) {
    console.error(
      "Error fetching Transactions & Allocations statistics:",
      error
    );
    throw error;
  }
};

// Progressive loading: fetch 1000 at a time, call callback with each batch
export const getUnifiedTransactionsProgressive = async (
  filters = {},
  onBatch = null
) => {
  setAuthHeader();
  const batchSize = 1000;
  let allResults = [];
  let totalCount = 0;
  let currentPage = 1;
  let hasMore = true;
  let firstBatchDelivered = false;

  // Build base params (without pagination)
  const baseParams = new URLSearchParams();

  if (filters.brand_ids && Array.isArray(filters.brand_ids)) {
    baseParams.append("brand_ids", filters.brand_ids.join(","));
  }
  if (filters.include_bank !== undefined)
    baseParams.append("include_bank", String(filters.include_bank));
  if (filters.include_overpayment !== undefined)
    baseParams.append(
      "include_overpayment",
      String(filters.include_overpayment)
    );
  if (filters.include_cash !== undefined)
    baseParams.append("include_cash", String(filters.include_cash));
  if (filters.include_credit !== undefined)
    baseParams.append("include_credit", String(filters.include_credit));
  if (filters.start_date) baseParams.append("start_date", filters.start_date);
  if (filters.end_date) baseParams.append("end_date", filters.end_date);

  try {
    while (hasMore) {
      const params = new URLSearchParams(baseParams);
      params.set("page", String(currentPage));
      params.set("page_size", String(batchSize));

      const path = `api/unified-transactions/?${params.toString()}`;
      const response = await accountingAPI.get(path);
      const data = response.data;

      const pageResults = data?.results || [];
      const pageCount = data?.count || 0;

      if (currentPage === 1) {
        totalCount = pageCount;
      }

      allResults = [...allResults, ...pageResults];

      // Call callback with batch if provided - use setTimeout to allow UI to update
      if (onBatch && typeof onBatch === "function") {
        const isFirst = !firstBatchDelivered;
        if (isFirst) {
          // First batch: call immediately
          onBatch(pageResults, {
            currentPage,
            totalFetched: allResults.length,
            totalCount: totalCount || allResults.length,
            isFirstBatch: true,
          });
          firstBatchDelivered = true;
        } else {
          // Subsequent batches: allow UI to update between batches
          await new Promise((resolve) => setTimeout(resolve, 0));
          onBatch(pageResults, {
            currentPage,
            totalFetched: allResults.length,
            totalCount: totalCount || allResults.length,
            isFirstBatch: false,
          });
        }
      }

      if (pageResults.length === 0) {
        hasMore = false;
      } else if (pageResults.length === batchSize) {
        currentPage++;
        if (currentPage > 1000) {
          hasMore = false;
        }
      } else {
        hasMore = false;
      }
    }

    return { results: allResults, count: totalCount || allResults.length };
  } catch (error) {
    console.error("Error fetching unified transactions:", error);
    throw error;
  }
};

// Chart data for dashboard visualizations
export const getChartData = async (chartType, filters = {}) => {
  const params = new URLSearchParams();

  params.append("chart_type", chartType);
  if (filters.brand_ids && Array.isArray(filters.brand_ids)) {
    params.append("brand_ids", filters.brand_ids.join(","));
  }
  if (filters.client_ids && Array.isArray(filters.client_ids)) {
    params.append("client_ids", filters.client_ids.join(","));
  }
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  if (filters.period) params.append("period", filters.period);

  try {
    const path = `api/chart-data/?${params.toString()}`;
    return await cachedGetData(path, 60000); // 60s cache
  } catch (error) {
    console.error(`Error fetching chart data for ${chartType}:`, error);
    throw error;
  }
};
